import Cart from "cart-store";

const STEPS = [
  { id: 1, label: "How to Start" },
  { id: 2, label: "The Item" },
  { id: 3, label: "Photos" },
  { id: 4, label: "Repair" },
  { id: 5, label: "Measure" },
  { id: 6, label: "Make It Yours" },
  { id: 7, label: "Review" },
];

const ITEM_TYPES = ["Jeans", "Jacket", "Shirt or Blouse", "Pants", "Skirt or Dress", "Sweater or Knit", "Other"];
const MATERIALS = [
  "Denim",
  "Cotton",
  "Linen",
  "Silk",
  "Wool",
  "Cashmere",
  "Other Natural Fiber",
  "Mixed or Unknown",
];
const MATERIALS_WITHOUT_DENIM = MATERIALS.filter((item) => item !== "Denim");
const STRETCH = ["No", "A Little", "Yes", "I’m Not Sure"];
const LOCATIONS = [
  "Knee or Leg",
  "Seat or Hip",
  "Crotch",
  "Pocket",
  "Elbow or Sleeve",
  "Seam",
  "Cuff, Hem or Waistband",
  "Body of Garment",
  "Cluster of Small Holes or Spots",
  "Other",
];

const BOTTOM_ONLY_LOCATIONS = ["Knee or Leg", "Seat or Hip", "Crotch"];
const SLEEVE_ONLY_LOCATIONS = ["Elbow or Sleeve"];

function locationsFor(itemType) {
  if (itemType === "Jacket" || itemType === "Shirt or Blouse" || itemType === "Sweater or Knit") {
    return LOCATIONS.filter((item) => !BOTTOM_ONLY_LOCATIONS.includes(item));
  }
  if (itemType === "Jeans" || itemType === "Pants") {
    return LOCATIONS.filter((item) => !SLEEVE_ONLY_LOCATIONS.includes(item));
  }
  return LOCATIONS;
}

function locationList(state) {
  if (Array.isArray(state.location)) return state.location.filter(Boolean);
  return state.location ? [state.location] : [];
}

function locationLabel(state) {
  return locationList(state).join(", ");
}

const MATERIALS_BY_ITEM = {
  Jeans: ["Denim", "Mixed or Unknown"],
  Jacket: MATERIALS,
  "Shirt or Blouse": MATERIALS,
  Pants: MATERIALS,
  "Skirt or Dress": MATERIALS,
  "Sweater or Knit": MATERIALS_WITHOUT_DENIM,
  Other: MATERIALS,
};

function materialsFor(itemType) {
  return MATERIALS_BY_ITEM[itemType] || MATERIALS;
}
const AREA_COUNTS = ["One", "Two", "Three", "Four or more"];
const KNIT_MOTIFS = ["Roses", "Stars", "Raindrops"];

const PRODUCT_MAP = {
  denim: {
    patch: "denim-repair-patch-and-mend",
    tear: "denim-repair-clean-tear",
    send_mend: "denim-repair-send-mend-deposit",
    whole_enchilada: "denim-repair-whole-enchilada",
    custom: "denim-repair-custom-deposit",
  },
  woven: {
    patch: "woven-repair-patch-mend",
    tear: "woven-repair-clean-tear",
    leaf: "woven-repair-embroidered-leaf",
    appliques: "embroidered-appliques",
    send_mend: "woven-repair-send-mend-deposit",
    custom: "woven-repair-custom-deposit",
  },
  knit: {
    embroidered: "sweater-knit-repair-embroidered-repair",
    leaf: "sweater-knit-repair-embroidered-repair",
    trim: "sweater-knit-repair-trim",
    send_mend: "sweater-and-knit-repair-send-mend",
    custom: "sweater-and-knit-repair-custom-deposit",
  },
};

const EXTRA_PRODUCT_HANDLES = [
  "other-repair-deposit",
  "other-repair-custom-deposit",
  "buttonhole-stitch-around-perimeter",
  "metallic-thread",
  "premium-stitch",
  "30-minute-consult",
];
const CONSULT_BOOKING_URL = "https://calendly.com/consult-surplusatx/30-min-consult";
const CUSTOM_PRODUCT_IDS = [8146192531549, 8147448397917, 8147440369757];

function isCustomShopifyProduct(prod) {
  return CUSTOM_PRODUCT_IDS.includes(Number(prod?.id));
}

const STANDARD_STITCHES = [
  "Interlocking Circles",
  "Diamond Grid",
  "Lotus",
  "Plusses",
  "Dashes",
  "Starburst",
];

const PREMIUM_STITCHES = ["Dots", "Floral Tile", "Molecule", "Windmills", "Bamboo", "Scallops"];

const PERIMETER_STITCHES = ["Barbed Wire", "Crosshatch", "Saddle Stitch", "Running Stitch"];

const TEAR_STITCHES = ["Barbed Wire", "Feather", "Crosshatch", "Sutures"];


const PATCH_PLACEMENTS = [
  {
    key: "Over-Patch",
    title: "Over-Patch",
    text: "The patch sits on top and becomes part of the design. A Running Stitch around the perimeter is included.",
  },
  {
    key: "Under-Mend",
    title: "Under-Mend",
    text: "The supporting patch sits behind the damaged fabric.",
  },
];

const REPAIR_DIRECTIONS = [
  { key: "Tonal", title: "Tonal", text: "Blends with the garment for a quieter overall repair." },
  { key: "Pop", title: "Pop", text: "Uses visible contrast to make the repair part of the design." },
];

const DENIM_PALETTES = [
  { key: "tonal", title: "Tonal", swatch: "denim-neutrals-mix", text: "Subtle shades that stay close to the original denim." },
  { key: "contrast", title: "Contrast", swatch: "denim-blues-mix", text: "A bolder color contrast within the denim patch." },
  { key: "mix", title: "Mix", swatch: "denim-mix-1", text: "A complementary blend of denim colors." },
];

const PATTERNED_ORDER = [
  "black-grey",
  "white-cream",
  "blues",
  "greens",
  "reds",
  "orange-yellow",
  "brown-tan",
  "custom",
];

const PATTERNED_TITLES = {
  "black-grey": "Black-Charcoal",
  "white-cream": "White-Cream",
  blues: "Blues",
  greens: "Greens",
  reds: "Red-Pink",
  "orange-yellow": "Orange-Yellow",
  "brown-tan": "Brown-Tan",
  custom: "Prints",
};

function areaLimitCopy(family) {
  const base = "For one to four separate areas. Each area is its own sized repair.";
  if (family === "denim") {
    return `${base} Whole Enchilada is available as a full-coverage denim rebuild. Damage over six inches needs Custom Denim.`;
  }
  if (family === "knit") return `${base} Damage over six inches needs Custom Sweater.`;
  return `${base} Damage over six inches needs Custom Woven.`;
}

const THREAD_COLORS = [
  ["Black-Charcoal", "#292929"],
  ["White-Cream", "#F3EBDD"],
  ["Light Blue", "#8FC5E8"],
  ["Dark Blue", "#243B5A"],
  ["Royal Blue", "#2855C7"],
  ["Green Apple", "#91BE3E"],
  ["Army Green", "#596247"],
  ["Grass Green", "#439447"],
  ["Mint", "#A8D8C1"],
  ["Red", "#C83C3C"],
  ["Raspberry", "#B83267"],
  ["Pink", "#E58EAD"],
  ["Purple", "#73569B"],
  ["Orange", "#E8752E"],
  ["Yellow", "#E5B934"],
  ["Brown", "#76523D"],
  ["Tan", "#C5A37E"],
];

const METALLIC_COLORS = [
  ["Rich Metallic Gold", "#D4AF37", "Gold"],
  ["Gilded Sandstone", "#C2A179", "Gold"],
  ["Brass", "#B0892D", "Gold"],
  ["Bright Gold", "#EFBF04", "Gold"],
  ["Metallic Silver", "#BCC6CC", "Silver"],
  ["Shaded Silver", "#999B9B", "Silver"],
  ["Standard Silver", "#C0C0C0", "Silver"],
  ["Cool Metallic Silver", "#A8A9AD", "Silver"],
  ["Brushed Nickel", "#E5E7EB", "Silver"],
  ["Copper Ember", "#B24A2E", "Copper"],
  ["Rose Gold", "#C27A7A", "Copper"],
  ["Bronze Copper", "#CE8946", "Copper"],
  ["Light Copper", "#D98F63", "Copper"],
  ["Rose Gold Light", "#DEA193", "Copper"],
  ["Platinum Frost", "#0F172A", "Steel"],
  ["Gunmetal Nickel", "#1F2937", "Steel"],
  ["Gunmetal Gray", "#353E43", "Steel"],
  ["Midnight Bronze", "#070A0F", "Steel"],
];

const DAMAGE_TYPES = [
  {
    key: "clean_tear",
    title: "Clean tear, rip, or split seam",
    text: "No missing material.",
  },
  {
    key: "hole",
    title: "Hole, worn area, or missing fabric",
    text: "The repair needs coverage and reinforcement.",
  },
  {
    key: "knit_edge",
    title: "Knit edge, cuff, hem, or opening",
    text: "The edge needs a clean, reinforced finish.",
  },
  {
    key: "spot",
    title: "Spot or stain",
    text: "No missing material.",
  },
];

const APPLIQUE_COUNTS = [
  { key: "1 appliqué", title: "1 appliqué" },
  { key: "2 appliqués", title: "2 appliqués" },
  { key: "3 appliqués", title: "3 appliqués" },
  { key: "4+", title: "4 or more" },
];

function damageTypesFor(family) {
  if (family === "knit") {
    return DAMAGE_TYPES.filter((item) => item.key !== "clean_tear");
  }
  return DAMAGE_TYPES.filter((item) => item.key !== "knit_edge");
}

const PHOTO_SLOTS = [
  {
    key: "full",
    number: "01",
    heading: "Whole Item",
    title: "Photo 1 — The whole item",
    text: "Show the full garment from top to bottom.",
    button: "Upload a full-garment photo",
  },
  {
    key: "damage",
    number: "02",
    heading: "Damage",
    title: "Photo 2 — The damage",
    text: "Move close enough for us to see the tear, hole, stain or worn area clearly.",
    button: "Upload a close-up",
  },
  {
    key: "scale",
    number: "03",
    heading: "Scale",
    title: "Photo 3 — The scale",
    text: "Place a hand beside the complete damaged or weakened area so we can get a sense of its size.",
    button: "Upload a scale photo",
  },
];

function photoSlotRequired() {
  return true;
}

const HIDDEN_STITCH_TYPES = ["Overlay", "Signature Overlay", "Perimeter Stitch", "Patch & Stitch"];

function formatMoney(cents) {
  if (cents == null || cents === "") return "";
  if (typeof cents === "string" && cents.trim().startsWith("$")) {
    return cents.trim().replace(/\.00\b/, "");
  }
  const dollars = Number(cents) / 100;
  if (!Number.isFinite(dollars)) return "";
  const amount = Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
  return `$${amount}`;
}

function compactMoney(cents) {
  return formatMoney(cents).replace(/^\$/, "");
}

function compactDollar(cents) {
  return formatMoney(cents);
}

function productPriceCents(item) {
  if (!item) return 0;
  const cents = Number(item.price ?? item.minPrice ?? 0);
  return Number.isFinite(cents) && cents > 0 ? cents : 0;
}

function priceLabel(item) {
  const cents = productPriceCents(item);
  return cents ? formatMoney(cents) : "";
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function product(products, handle) {
  return products[handle] && products[handle].id ? products[handle] : { variants: [], minPrice: 0, minPriceFormatted: "", title: "", handle };
}

function uniqueOptionValues(prod, optionKey) {
  const values = [];
  for (const variant of prod.variants || []) {
    const value = variant[optionKey];
    if (value && value !== "Default Title" && !HIDDEN_STITCH_TYPES.includes(value) && !values.includes(value)) {
      values.push(value);
    }
  }
  return values;
}

function visibleVariants(prod) {
  const all = prod.variants || [];
  const visible = all.filter((variant) => !HIDDEN_STITCH_TYPES.includes(variant.option2));
  return visible.length ? visible : all;
}

function findVariant(prod, option1, option2, option3) {
  const variants = visibleVariants(prod);
  if (!variants.length) return null;
  if (variants.length === 1 && (!variants[0].option1 || variants[0].option1 === "Default Title")) {
    return variants[0];
  }
  const matches = variants.filter((variant) => {
    const match1 = !option1 || variant.option1 === option1;
    const match2 = !option2 || variant.option2 === option2;
    const match3 = !option3 || variant.option3 === option3;
    return match1 && match2 && match3;
  });
  return matches[0] || variants.find((variant) => !option1 || variant.option1 === option1) || null;
}

const EDGE_LOCATIONS = ["Cuff, Hem or Waistband"];
const CONSTRUCTION_CHOICE_ITEMS = ["Jacket", "Shirt or Blouse", "Pants", "Skirt or Dress", "Other"];

function isEdgeLocation(location) {
  return EDGE_LOCATIONS.includes(location);
}

function needsConstructionChoice(state) {
  return state.material === "Cotton" && CONSTRUCTION_CHOICE_ITEMS.includes(state.itemType);
}

function syncConstruction(state) {
  if (state.material === "Cotton" && state.itemType === "Sweater or Knit") {
    state.construction = "knit";
    return;
  }
  if (state.material !== "Cotton") state.construction = "";
}

function inferFamily(state) {
  if (state.material === "Denim") return "denim";
  if (["Wool", "Cashmere"].includes(state.material)) return "knit";
  if (state.material === "Cotton") {
    if (state.itemType === "Sweater or Knit" || state.construction === "knit") return "knit";
    return "woven";
  }
  if (["Linen", "Silk"].includes(state.material)) return "woven";
  return "";
}

function repairAllowed(family, productKey, location, damage) {
  if (!family || !productKey) return false;
  if (productKey === "tear") return (family === "denim" || family === "woven") && damage === "clean_tear";
  if (productKey === "trim") return family === "knit" && isEdgeLocation(location) && damage === "knit_edge";
  if (productKey === "patch") return family === "denim" || family === "woven";
  if (productKey === "whole_enchilada") return family === "denim";
  if (productKey === "leaf") return family === "woven" || family === "knit";
  if (productKey === "appliques") return family === "woven";
  if (productKey === "embroidered") return family === "knit";
  if (productKey === "custom") return family === "denim" || family === "woven" || family === "knit";
  return false;
}

function threadColorName(value) {
  return String(value || "").replace(/\s*\(#[0-9A-Fa-f]{3,8}\)\s*$/, "").trim();
}

function stretchIsUnsure(stretch) {
  return stretch === "I’m Not Sure" || stretch === "I’m not sure";
}

function materialNeedsReview(material) {
  return ["Unknown", "Mixed or Unknown", "Other Natural Fiber"].includes(material);
}

function locationNeedsReview(state) {
  return locationList(state).some((item) => item === "Crotch" || item === "Other");
}

function eligibility(state) {
  if (
    state.stretch === "Yes" ||
    stretchIsUnsure(state.stretch) ||
    materialNeedsReview(state.material) ||
    state.itemType === "Other" ||
    locationNeedsReview(state)
  ) {
    return "review";
  }
  return "ok";
}

function tearTypeLabel(tearType) {
  const key = tearType === "spots" ? "spot" : tearType;
  return DAMAGE_TYPES.find((item) => item.key === key)?.title || "";
}

function effectiveMeasureKind(state) {
  if (state.tearType === "hole" || state.tearType === "spots") return "patch";
  if (state.tearType === "both") return "both";
  if (state.tearType === "clean_tear") return "length";
  return state.measureKind;
}

function areaNeedsMeasure(record, state) {
  if (!record?.productKey) return false;
  if (["send_mend", "whole_enchilada", "custom"].includes(state.approach)) return false;
  if (["whole_enchilada", "custom", "embroidered", "appliques"].includes(record.productKey)) return false;
  if (record.productKey === "leaf" && inferFamily(state) === "knit") return false;
  return ["patch", "tear", "leaf", "trim"].includes(record.productKey);
}

function skipMeasure(state) {
  if (["send_mend", "whole_enchilada", "custom"].includes(state.approach)) return true;
  const records = (state.areaRecords || []).filter((record) => record.productKey);
  if (records.length) return records.every((record) => !areaNeedsMeasure(record, state));
  if (["whole_enchilada", "custom", "embroidered", "appliques"].includes(state.productKey)) return true;
  if (state.productKey === "leaf" && inferFamily(state) === "knit") return true;
  return false;
}

function skipProducts(state) {
  return state.approach === "whole_enchilada" || state.approach === "custom";
}

function skipDetails(state) {
  if (state.productKey === "custom") return true;
  return (state.areaRecords || []).some((record) => record.productKey === "custom");
}

const CUSTOM_STARTING_AT = "Starting at $150";

function emptyAreaRecord(location = "") {
  return { location, damage: "", productKey: "", size: "", appliqueCount: "" };
}

function approachLabel(approach) {
  if (approach === "send_mend") return "Send & Mend";
  if (approach === "whole_enchilada") return "Whole Enchilada";
  if (approach === "custom") return "Custom Repair";
  return "Measure & Mend";
}

function areaCount(areas) {
  if (areas === "Two") return 2;
  if (areas === "Three") return 3;
  if (areas === "Four" || areas === "Four or more" || areas === "Five or More") return 4;
  return 1;
}

function isOverSixInches(state) {
  return (
    state.size === "over6" ||
    (state.areaSizes || []).includes("over6") ||
    (state.areaRecords || []).some((record) => record.size === "over6")
  );
}

function needsOversize(state) {
  return isOverSixInches(state);
}

function usesCustomDeposit(state) {
  if (state.productKey === "whole_enchilada") return false;
  if (state.productKey === "custom") return true;
  if ((state.areaRecords || []).some((record) => record.productKey === "custom")) return true;
  if (state.approach === "send_mend") return false;
  if (isOverSixInches(state)) return true;
  return false;
}

function displaySize(size) {
  if (size === "over6") return "More than 6 inches";
  return size || "";
}

function metallicMode(state) {
  if (state.productKey === "leaf" && inferFamily(state) === "woven") return "full";
  if (state.productKey === "appliques") return "accent";
  return "";
}

function allowsMetallic(state) {
  return Boolean(metallicMode(state));
}

function isPremiumStitch(name) {
  return PREMIUM_STITCHES.includes(name);
}

function isPerimeterStitch(name) {
  return PERIMETER_STITCHES.includes(name);
}

function metallicColorEntry(value) {
  return METALLIC_COLORS.find(([name, hex]) => value === `${name} (${hex})`);
}

function isMetallicThread(state) {
  return allowsMetallic(state) && Boolean(state.metallic) && state.metallic !== "None";
}

function metallicByRequest(state) {
  return state.productKey === "custom" && state.family === "knit";
}

function leafSizeValue(size) {
  if (!size || size === "over6") return "";
  if (String(size).includes("Leaf")) return size;
  const inches = String(size).match(/\d+/);
  return inches ? `${inches[0]}" Leaf` : size;
}

function leafMaterialValue(material) {
  if (["Cotton", "Linen", "Silk"].includes(material)) return material;
  if (material === "Other" || material === "Other Natural Fiber") return "Other";
  return "Cotton";
}

function sendMendOption(productKey, damage) {
  if (damage === "spot" || damage === "spots") return "Spots";
  if (productKey === "trim" || damage === "knit_edge") return "Trim Repair";
  if (["patch", "embroidered", "appliques", "leaf"].includes(productKey)) return "Holes";
  return "Clean Tear";
}

const FUNNEL_STORAGE_KEY = "surplusatx-repair-form";

function defaultFormState() {
  return {
    step: 1,
    route: "",
    acknowledged: false,
    itemType: "",
    material: "",
    construction: "",
    stretch: "",
    location: [],
    areas: "",
    areaRecords: [],
    areaIndex: 0,
    approach: "",
    family: "",
    photos: { full: "", damage: "", scale: "" },
    measureKind: "",
    tearType: "",
    size: "",
    areaSizes: [],
    productKey: "",
    appliqueCount: "",
    patchWanted: "",
    motifCount: "",
    stitchDesign: "",
    customStitch: "",
    threadColor: "",
    metallic: "",
    metallicRequest: "",
    patchMaterial: "",
    patchPlacement: "",
    patchColor: "",
    patchColorFamily: "",
    repairDirection: "",
    notes: "",
    buttonhole: "No",
    consultType: "",
    maxStep: 1,
  };
}

function restoreFormState() {
  try {
    const raw = sessionStorage.getItem(FUNNEL_STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || typeof saved !== "object") return null;
    const state = defaultFormState();
    Object.keys(state).forEach((key) => {
      if (!(key in saved)) return;
      const value = saved[key];
      if (key === "photos") {
        if (!value || typeof value !== "object") return;
        state.photos = {
          full: typeof value.full === "string" ? value.full : "",
          damage: typeof value.damage === "string" ? value.damage : "",
          scale: typeof value.scale === "string" ? value.scale : "",
        };
        return;
      }
      if (key === "location" || key === "areaSizes") {
        if (Array.isArray(value)) {
          state[key] = value
            .filter((item) => typeof item === "string")
            .map((item) =>
              item === "Multiple Damage Areas" || item === "Several areas" ? "Cluster of Small Holes or Spots" : item
            );
        }
        return;
      }
      if (key === "areaRecords") {
        if (!Array.isArray(value)) return;
        state.areaRecords = value
          .filter((item) => item && typeof item === "object")
          .map((item) => ({
            location: typeof item.location === "string" ? item.location : "",
            damage: typeof item.damage === "string" ? (item.damage === "spots" ? "spot" : item.damage) : "",
            productKey: typeof item.productKey === "string" ? item.productKey : "",
            size: typeof item.size === "string" ? item.size : "",
            appliqueCount: typeof item.appliqueCount === "string" ? item.appliqueCount : "",
          }));
        return;
      }
      if (key === "areaIndex") {
        const index = Number(value);
        if (Number.isInteger(index) && index >= 0) state.areaIndex = index;
        return;
      }
      if (key === "step") {
        const step = Number(value);
        if (step >= 1 && step <= 7) state.step = step;
        return;
      }
      if (key === "acknowledged") {
        state.acknowledged = Boolean(value);
        return;
      }
      if (typeof value === typeof state[key]) state[key] = value;
    });
    if (state.areas === "Five or More" || state.areas === "Four") state.areas = "Four or more";
    if (state.tearType === "spots") state.tearType = "spot";
    if (state.tearType === "both") state.tearType = "";
    if (state.threadColor) state.threadColor = threadColorName(state.threadColor);
    syncConstruction(state);
    state.family = inferFamily(state);
    return state;
  } catch (error) {
    return null;
  }
}

function persistFormState(state) {
  try {
    sessionStorage.setItem(FUNNEL_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    // Ignore quota / private-mode failures.
  }
}

function choiceMark() {
  return `<span class="repair-form__choice-mark" aria-hidden="true"><svg viewBox="0 0 16 16" width="14" height="14" focusable="false"><path fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" d="M3.5 8.2 6.6 11.3 12.5 4.7"/></svg></span>`;
}

function choiceButton(value, title, meta, selected) {
  return `
    <button type="button" class="repair-form__choice${selected ? " is-selected" : ""}" data-choice="${escapeHtml(value)}">
      ${choiceMark()}
      <span class="repair-form__choice-title">${escapeHtml(title)}</span>
      ${meta ? `<span class="repair-form__choice-meta">${escapeHtml(meta)}</span>` : ""}
    </button>
  `;
}

function fieldLabel(text, required = false) {
  return `<label class="repair-form__label">${escapeHtml(text)}${required ? " *" : ""}</label>`;
}

function fieldGroup(label, body, required = false, currentId = "") {
  return `<div class="repair-form__group"${
    currentId ? ` data-details-current="${escapeHtml(currentId)}"` : ""
  }>${fieldLabel(label, required)}${body}</div>`;
}

function swatchRow(colors, selected, field) {
  const selectedName = threadColorName(selected);
  return `
    <div class="repair-form__swatches" data-field="${field}">
      ${colors
        .map(([name, hex]) => {
          const isSelected = selectedName === name;
          return `<button type="button" class="repair-form__swatch${isSelected ? " is-selected" : ""}" data-choice="${escapeHtml(
            name
          )}" style="--swatch:${hex}" title="${escapeHtml(name)}" aria-label="${escapeHtml(name)}">${choiceMark()}</button>`;
        })
        .join("")}
    </div>
  `;
}

function photoName(url) {
  if (!url) return "";
  const parts = String(url).split("/").filter(Boolean);
  return parts[parts.length - 1] || "Uploaded photo";
}

function designButton(name, image, selected) {
  return `
    <button type="button" class="repair-form__design${selected ? " is-selected" : ""}" data-choice="${escapeHtml(name)}">
      ${choiceMark()}
      ${image ? `<img src="${escapeHtml(image)}" alt="" width="200" height="200">` : ""}
      <span class="repair-form__choice-title">${escapeHtml(name)}</span>
    </button>
  `;
}

function imageChoice(value, title, image, selected, meta = "", { showTitle = true } = {}) {
  return `
    <button type="button" class="repair-form__photo-choice${selected ? " is-selected" : ""}" data-choice="${escapeHtml(value)}" aria-label="${escapeHtml(title || value)}">
      ${choiceMark()}
      ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(title || "")}" width="400" height="400">` : ""}
      ${showTitle && title ? `<span class="repair-form__choice-title">${escapeHtml(title)}</span>` : ""}
      ${meta ? `<span class="repair-form__choice-meta">${escapeHtml(meta)}</span>` : ""}
    </button>
  `;
}

class RepairForm {
  constructor(root, config) {
    this.root = root;
    this.config = config;
    this.products = config.products || {};
    this.stitchImages = config.stitchImages || {};
    this.media = config.media || {};
    this.patchLibrary = Array.isArray(config.patchLibrary) ? config.patchLibrary : [];
    this.panel = root.querySelector("[data-panel]");
    this.progress = root.querySelector("[data-progress]");
    this.progressBar = root.querySelector(".repair-form__progress-bar");
    this.progressStatus = root.querySelector("[data-progress-status]");
    this.errorEl = root.querySelector("[data-error]");
    this.backBtn = root.querySelector("[data-back]");
    this.nextBtn = root.querySelector("[data-next]");
    this.navEl = root.querySelector(".repair-form__nav");
    this.priceEl = root.querySelector("[data-price-summary]");
    this._funnelKey = null;
    this._detailsStage = null;
    this.state = restoreFormState() || defaultFormState();

    this.backBtn.addEventListener("click", () => this.onBack());
    this.nextBtn.addEventListener("click", () => this.onNext());
    this.progress?.addEventListener("click", (event) => this.onProgressClick(event));
    this.panel.addEventListener("click", (event) => this.onPanelClick(event));
    this.panel.addEventListener("change", (event) => this.onPanelChange(event));
    this.panel.addEventListener("input", (event) => this.onPanelChange(event));
    window.addEventListener("pageshow", () => this.resetBusy());
    this.hydrateExtraProducts().then(() => this.render());
  }

  async hydrateExtraProducts() {
    const root = this.config.root === "/" ? "" : this.config.root.replace(/\/$/, "");
    await Promise.all(
      EXTRA_PRODUCT_HANDLES.map(async (handle) => {
        if (this.products[handle]?.id) return;
        try {
          const response = await fetch(`${root}/products/${handle}.js`);
          if (!response.ok) return;
          const fetched = await response.json();
          const prices = (fetched.variants || []).map((variant) => variant.price);
          this.products[handle] = {
            id: fetched.id,
            handle: fetched.handle,
            title: fetched.title,
            available: fetched.available,
            minPrice: Math.min(...prices),
            minPriceFormatted: formatMoney(Math.min(...prices)),
            variants: (fetched.variants || []).map((variant) => ({
              id: variant.id,
              title: variant.title,
              option1: variant.option1,
              option2: variant.option2,
              option3: variant.option3,
              price: variant.price,
              priceFormatted: formatMoney(variant.price),
              available: variant.available,
            })),
          };
        } catch (error) {
          console.warn("Could not load repair product", handle, error);
        }
      })
    );
  }

  showError(message) {
    this.errorEl.hidden = !message;
    this.errorEl.textContent = message || "";
  }

  metallicAddOn() {
    return product(this.products, "metallic-thread");
  }

  premiumAddOn() {
    return product(this.products, "premium-stitch");
  }

  mediaSrc(group, key) {
    return this.media?.[group]?.[key] || "";
  }

  patchGroups() {
    return this.patchLibrary;
  }

  patchGroup(key) {
    return this.patchGroups().find((item) => item.key === key);
  }

  patchSwatches(groupKey) {
    return this.patchGroup(groupKey)?.swatches || [];
  }

  patchMaterialLabel(key) {
    return (
      this.patchMaterialChoices().find((item) => item.key === key)?.title ||
      this.patchGroup(key)?.title ||
      PATTERNED_TITLES[key] ||
      key
    );
  }

  patchPaletteLabel(key) {
    const denim = DENIM_PALETTES.find((item) => item.key === key);
    if (denim) return denim.title;
    if (key === "matching-linen") return "Matching linen, Surplus-selected";
    if (key === "surplus-silk") return "Silk, Surplus-selected";
    for (const group of this.patchGroups()) {
      const swatch = (group.swatches || []).find((item) => item.key === key);
      if (swatch) {
        const familyTitle = PATTERNED_TITLES[group.key] || group.title;
        const swatchTitle = String(swatch.title || "").startsWith("IMG_") ? familyTitle : swatch.title || familyTitle;
        return swatchTitle === familyTitle ? familyTitle : `${familyTitle} · ${swatchTitle}`;
      }
    }
    return PATTERNED_TITLES[key] || key;
  }

  patchColorSummary() {
    const family = PATTERNED_TITLES[this.state.patchColorFamily] || "";
    const color = this.patchPaletteLabel(this.state.patchColor);
    return [family, color].filter((value, index, list) => value && list.indexOf(value) === index).join(" · ");
  }

  patchLibrarySrc(groupKey, swatchKey) {
    const group = this.patchGroup(groupKey);
    if (!group) return "";
    if (!swatchKey) return group.src || "";
    return (group.swatches || []).find((item) => item.key === swatchKey)?.src || group.src || "";
  }

  patchMaterialChoices() {
    if (this.state.productKey !== "patch") return [];
    if (this.state.material === "Silk") return [];
    const denimSrc = this.patchLibrarySrc("denim", "denim-blues-mix") || this.patchLibrarySrc("denim");
    const wovenSrc = this.patchLibrarySrc("orange-yellow");
    const linenSrc = this.patchLibrarySrc("linen");
    const denimChoice = { key: "denim-patch", title: "Denim Patch", src: denimSrc };
    const wovenChoice = { key: "patterned-cotton", title: "Patterned Cotton Woven Patch", src: wovenSrc };
    const linenChoice = { key: "matching-linen", title: "Matching Linen", src: linenSrc };
    if (this.family() === "denim" || this.state.material === "Denim") return [denimChoice, wovenChoice];
    if (this.state.material === "Linen") return [linenChoice, wovenChoice];
    return [wovenChoice];
  }

  patternedGroups() {
    const byKey = Object.fromEntries(this.patchGroups().map((group) => [group.key, group]));
    return PATTERNED_ORDER.map((key) => {
      const group = byKey[key];
      if (!group) return null;
      return { ...group, title: PATTERNED_TITLES[key] || group.title };
    }).filter(Boolean);
  }

  denimPaletteChoices() {
    return DENIM_PALETTES.map((item) => ({
      ...item,
      src: this.patchLibrarySrc("denim", item.swatch),
    }));
  }

  patchNeedsMaterialChoice() {
    return this.patchMaterialChoices().length > 1;
  }

  patchNeedsFamilyChoice() {
    return this.state.patchMaterial === "patterned-cotton";
  }

  patchNeedsColorChoice() {
    return this.state.patchMaterial === "denim-patch" || this.state.patchMaterial === "patterned-cotton";
  }

  detailsFlow() {
    const s = this.state;
    const key = s.productKey;
    if (key === "patch") {
      this.state.patchWanted = "Yes";
      const flow = ["placement"];
      if (this.patchNeedsMaterialChoice()) flow.push("material");
      if (s.patchMaterial === "denim-patch") flow.push("denimPalette");
      if (this.patchNeedsFamilyChoice()) {
        flow.push("family");
        flow.push("fabric");
      }
      flow.push("stitch", "thread", "direction", "notes");
      return flow;
    }
    if (key === "tear") return ["stitch", "thread", "direction", "notes"];
    if (key === "embroidered" || key === "appliques") {
      const flow = ["stitch", "thread"];
      if (metallicMode(s) === "accent") flow.push("metallic");
      flow.push("direction", "notes");
      return flow;
    }
    if (key === "custom") return ["notes"];
    if (key === "whole_enchilada") return ["consult", "notes"];
    if (key === "leaf") {
      const flow = ["visual", "thread"];
      if (metallicMode(s) === "full") flow.push("metallic");
      flow.push("direction", "notes");
      return flow;
    }
    if (key === "trim") return ["visual", "thread", "notes"];
    return ["thread", "notes"];
  }

  detailComplete(id) {
    const s = this.state;
    if (id === "patchWanted") return Boolean(s.patchWanted);
    if (id === "placement") return Boolean(s.patchPlacement);
    if (id === "material") return Boolean(s.patchMaterial);
    if (id === "denimPalette" || id === "fabric") return Boolean(s.patchColor);
    if (id === "family") return Boolean(s.patchColorFamily);
    if (id === "stitch") return Boolean(s.stitchDesign) && (s.productKey !== "patch" || !isPerimeterStitch(s.stitchDesign));
    if (id === "customStitch") return Boolean(s.customStitch);
    if (id === "motifs") return Boolean(s.motifCount);
    if (id === "visual") return true;
    if (id === "thread") return Boolean(s.threadColor);
    if (id === "metallic") return Boolean(s.metallic);
    if (id === "direction") return Boolean(s.repairDirection);
    if (id === "consult") return Boolean(s.consultType);
    if (id === "notes") return false;
    return true;
  }

  detailsCurrentId() {
    const flow = this.detailsFlow();
    return flow.find((id) => !this.detailComplete(id)) || flow[flow.length - 1] || "";
  }

  detailsReached(id) {
    const flow = this.detailsFlow();
    const current = this.detailsCurrentId();
    return flow.indexOf(id) !== -1 && flow.indexOf(id) <= flow.indexOf(current);
  }

  detailsStage(id) {
    return this.detailsIsCurrent(id) ? id : "";
  }

  detailsIsCurrent(id) {
    return this.detailsCurrentId() === id;
  }

  itemFlow() {
    const flow = ["itemType", "material"];
    if (needsConstructionChoice(this.state)) flow.push("construction");
    flow.push("stretch", "location", "areas");
    return flow;
  }

  itemComplete(id) {
    const s = this.state;
    if (id === "itemType") return Boolean(s.itemType);
    if (id === "material") return Boolean(s.material);
    if (id === "construction") return Boolean(s.construction);
    if (id === "stretch") return Boolean(s.stretch);
    if (id === "location") return locationList(s).length > 0;
    if (id === "areas") return Boolean(s.areas);
    return true;
  }

  itemCurrentId() {
    const flow = this.itemFlow();
    return flow.find((id) => !this.itemComplete(id)) || flow[flow.length - 1] || "";
  }

  itemReached(id) {
    const flow = this.itemFlow();
    const current = this.itemCurrentId();
    return flow.indexOf(id) !== -1 && flow.indexOf(id) <= flow.indexOf(current);
  }

  itemStage(id) {
    return this.itemCurrentId() === id ? id : "";
  }

  funStuffLink() {
    const root = this.config.root === "/" ? "" : this.config.root.replace(/\/$/, "");
    return `<div class="repair-form__note">
      <p>Want examples before you choose? <a href="${root}/pages/make-it-yours">Customization 101</a> shows how the options behave without making this step harder than it needs to be.</p>
    </div>`;
  }

  syncPatchDefaults() {
    if (this.state.productKey !== "patch") return;
    this.state.patchWanted = "Yes";
    if (this.state.material === "Silk") {
      this.state.patchMaterial = "surplus-silk";
      this.state.patchColor = "surplus-silk";
      this.state.patchColorFamily = "";
      return;
    }
    const choices = this.patchMaterialChoices();
    if (choices.length === 1 && this.state.patchMaterial !== choices[0].key) {
      this.state.patchMaterial = choices[0].key;
    }
    if (this.state.patchMaterial === "matching-linen") {
      this.state.patchColor = "matching-linen";
      this.state.patchColorFamily = "";
    }
  }

  stitchLibrary(name) {
    const buttons = (list) =>
      `<div class="repair-form__designs" data-field="stitchDesign">${list
        .map((item) => designButton(item, this.stitchImages[item], this.state.stitchDesign === item))
        .join("")}</div>`;
    if (name === "tear") return buttons(TEAR_STITCHES);
    const premiumCents = this.premiumCents();
    const premiumPrice = premiumCents ? `+${formatMoney(premiumCents)} per repair area` : "";
    const standard = `<p class="repair-form__designs-label">Standard — included</p>${buttons(STANDARD_STITCHES)}`;
    const premium = `<p class="repair-form__designs-label">Premium${
      premiumPrice ? ` <span>${escapeHtml(premiumPrice)}</span>` : ""
    }</p>${buttons(PREMIUM_STITCHES)}`;
    const overPatchNote =
      name !== "tear" && this.state.patchPlacement === "Over-Patch"
        ? `<p class="repair-form__note">Over-Patch includes a Running Stitch around the perimeter. Choose one Standard or Premium overlay — Running Stitch is not a substitute for the overlay.</p>`
        : "";
    return `${overPatchNote}${standard}${premium}`;
  }

  notesField(currentId = "") {
    return fieldGroup(
      "Optional notes",
      `<p class="repair-form__note">Anything you want us to know about color, pattern or visibility.</p>
      <textarea class="repair-form__notes" data-input="notes" rows="4" placeholder="Optional">${escapeHtml(
        this.state.notes || ""
      )}</textarea>`,
      false,
      currentId
    );
  }

  threadField(currentId = "") {
    const selected = threadColorName(this.state.threadColor);
    const selectedMarkup = selected
      ? `<span class="repair-form__selected-name">Selected: ${escapeHtml(selected)}</span>`
      : "";
    return `<div class="repair-form__group"${
      currentId ? ` data-details-current="${escapeHtml(currentId)}"` : ""
    }>
      <div class="repair-form__label-row">
        ${fieldLabel("Thread color", true)}
        ${selectedMarkup}
      </div>
      <p class="repair-form__note">Choose from the approved color library.</p>
      ${swatchRow(THREAD_COLORS, selected, "threadColor")}
    </div>`;
  }

  metallicField(currentId = "") {
    const mode = metallicMode(this.state);
    if (!mode) return "";
    const unit = this.metallicCents();
    const title = mode === "accent" ? "Metallic accent" : "Metallic thread";
    const note =
      mode === "accent"
        ? `Metallic is an accent only, not the primary thread.${unit ? ` +${formatMoney(unit)} per repair area.` : ""}`
        : `Metallic may be the full thread treatment.${unit ? ` +${formatMoney(unit)} per repair area.` : ""}`;
    return fieldGroup(
      title,
      `<p class="repair-form__note">${escapeHtml(note)}</p>
      <div class="repair-form__grid repair-form__grid--compact" data-field="metallic">
        ${["None", "Gold", "Silver", "Copper"]
          .map((item) =>
            choiceButton(item, item, item === "None" || !unit ? "" : `+${formatMoney(unit)}`, this.state.metallic === item)
          )
          .join("")}
      </div>
      ${isMetallicThread(this.state) ? this.selectionPreview("", this.metallicExample()) : ""}`,
      true,
      currentId
    );
  }

  motifImage(name) {
    return this.stitchImages[name] || "";
  }

  placementSrc(key) {
    if (key === "Over-Patch" && this.family() === "denim") {
      return this.mediaSrc("placements", "Over-Patch-denim") || this.mediaSrc("placements", key);
    }
    return this.mediaSrc("placements", key);
  }

  metallicExample() {
    if (this.state.metallic === "Gold") return this.mediaSrc("examples", "metallic-gold");
    if (this.state.metallic === "Silver") return this.mediaSrc("examples", "metallic-silver");
    return this.mediaSrc("examples", "metallic");
  }

  leafExample() {
    if (this.state.material === "Silk") return this.mediaSrc("examples", "leaf-silk");
    if (this.family() === "knit") return this.mediaSrc("examples", "leaf-knit");
    return this.mediaSrc("examples", "leaf-linen");
  }

  trimPreview() {
    const edge = this.mediaSrc("examples", "trim");
    const saddle = this.mediaSrc("examples", "trim-saddle");
    if (!edge && !saddle) return "";
    return `
      <div class="repair-form__selection-preview">
        ${
          edge
            ? `<figure class="repair-form__selection-figure">
                <img src="${escapeHtml(edge)}" alt="Blanket stitch trim example">
                <figcaption>Blanket stitch</figcaption>
              </figure>`
            : ""
        }
        ${
          saddle
            ? `<figure class="repair-form__selection-figure">
                <img src="${escapeHtml(saddle)}" alt="Sweater edge trim example">
                <figcaption>Sweater edge</figcaption>
              </figure>`
            : ""
        }
      </div>
    `;
  }

  selectionPreview(name, exampleUrl) {
    const art = name ? this.stitchImages[name] : "";
    const src = art || exampleUrl;
    if (!src) return "";
    const caption = art ? "Stitch diagram" : "Repair example";
    return `
      <div class="repair-form__selection-preview repair-form__selection-preview--single">
        <figure class="repair-form__selection-figure">
          <img src="${escapeHtml(src)}" alt="${escapeHtml(name || "Repair")} ${art ? "diagram" : "example"}">
          <figcaption>${caption}</figcaption>
        </figure>
      </div>
    `;
  }

  repairAreaCount() {
    const sizes = this.selectedSizes();
    if (sizes.length) return sizes.length;
    return Math.max(1, Math.min(4, areaCount(this.state.areas) || 1));
  }

  premiumCents() {
    return productPriceCents(this.premiumAddOn());
  }

  metallicCents() {
    return productPriceCents(this.metallicAddOn());
  }

  metallicHeading() {
    const unit = this.metallicCents();
    const areas = this.repairAreaCount();
    if (!unit) return "Metallic Thread";
    if (areas > 1) {
      return `Metallic Thread +${compactMoney(unit)} per repair area (${formatMoney(unit * areas)} for ${areas} areas)`;
    }
    return `Metallic Thread +${compactMoney(unit)} per repair area`;
  }

  checkoutAddOns() {
    const addOns = [];
    const areas = this.repairAreaCount();
    if (this.state.productKey === "patch" && this.state.buttonhole === "Yes") {
      const addOn = product(this.products, "buttonhole-stitch-around-perimeter");
      if (addOn.id) addOns.push(addOn);
    }
    if (this.state.productKey === "patch" && isPremiumStitch(this.state.stitchDesign)) {
      const addOn = this.premiumAddOn();
      const unit = this.premiumCents();
      if (addOn.id && unit) {
        const cents = unit * areas;
        addOns.push({
          title: areas > 1 ? `${addOn.title || "Premium Stitch"} × ${areas}` : addOn.title || "Premium Stitch",
          id: addOn.id,
          minPrice: cents,
          minPriceFormatted: formatMoney(cents),
        });
      }
    }
    if (allowsMetallic(this.state) && isMetallicThread(this.state)) {
      const addOn = this.metallicAddOn();
      const unit = this.metallicCents();
      if (addOn.id && unit) {
        const cents = unit * areas;
        addOns.push({
          title: areas > 1 ? `${addOn.title || "Metallic Thread"} × ${areas}` : addOn.title || "Metallic Thread",
          id: addOn.id,
          minPrice: cents,
          minPriceFormatted: formatMoney(cents),
        });
      }
    }
    if (this.needsLiveConsultFee()) {
      const consult = this.consultProduct();
      const variant = this.liveConsultVariant();
      const cents = productPriceCents(variant);
      if (consult.id && variant?.id && cents) {
        addOns.push({
          title: consult.title || "Live Consult",
          id: consult.id,
          minPrice: cents,
          minPriceFormatted: formatMoney(cents),
        });
      }
    }
    return addOns;
  }

  addOnTotalCents() {
    return this.checkoutAddOns().reduce((sum, addOn) => sum + Number(addOn.minPrice || addOn.variants?.[0]?.price || 0), 0);
  }

  family() {
    return this.state.family || inferFamily(this.state);
  }

  repairAreaTotal() {
    return Math.max(1, Math.min(4, areaCount(this.state.areas) || 1));
  }

  ensureAreaRecords() {
    const count = this.repairAreaTotal();
    const allowed = locationsFor(this.state.itemType);
    const fallback = locationList(this.state).find((item) => allowed.includes(item)) || "";
    if (!Array.isArray(this.state.areaRecords)) this.state.areaRecords = [];
    while (this.state.areaRecords.length < count) this.state.areaRecords.push(emptyAreaRecord(fallback));
    if (this.state.areaRecords.length > count) this.state.areaRecords = this.state.areaRecords.slice(0, count);
    this.state.areaRecords.forEach((record) => {
      if (!allowed.includes(record.location)) record.location = fallback;
    });
    if (!Number.isInteger(this.state.areaIndex) || this.state.areaIndex < 0) this.state.areaIndex = 0;
    if (this.state.areaIndex > count - 1) this.state.areaIndex = count - 1;
    return this.state.areaRecords;
  }

  currentArea() {
    const records = this.ensureAreaRecords();
    return records[this.state.areaIndex] || records[0];
  }

  syncCurrentArea(patch = {}) {
    const area = this.currentArea();
    Object.assign(area, patch);
    if (patch.productKey != null) this.state.productKey = patch.productKey;
    if (patch.damage != null) this.state.tearType = patch.damage;
    if (patch.size != null && this.state.areaIndex === 0) this.state.size = patch.size;
    if (patch.appliqueCount != null) this.state.appliqueCount = patch.appliqueCount;
    if (this.state.approach === "send_mend") {
      this.ensureAreaRecords().forEach((record) => {
        if (record === area) return;
        if (patch.productKey != null) record.productKey = patch.productKey;
        if (patch.damage != null) record.damage = patch.damage;
        if (patch.appliqueCount != null) record.appliqueCount = patch.appliqueCount;
        if (patch.size != null) record.size = patch.size;
        if (patch.location != null) record.location = patch.location;
      });
    }
    return area;
  }

  clearInvalidAreaRepair() {
    const area = this.currentArea();
    if (!area?.productKey) return;
    if (repairAllowed(this.family(), area.productKey, area.location, area.damage)) return;
    this.syncCurrentArea({ productKey: "", size: "", appliqueCount: "" });
    this.state.productKey = "";
    this.state.appliqueCount = "";
  }

  applyAreaToState(index = this.state.areaIndex) {
    const records = this.ensureAreaRecords();
    const area = records[index] || records[0];
    if (!area) return;
    this.state.productKey = area.productKey || "";
    this.state.tearType = area.damage || "";
    this.state.appliqueCount = area.appliqueCount || "";
    this.state.size = area.size || this.state.size;
  }

  checkoutRecords() {
    const records = this.ensureAreaRecords().filter((record) => record.productKey);
    if (records.some((record) => record.productKey === "whole_enchilada")) {
      return [records.find((record) => record.productKey === "whole_enchilada")];
    }
    if (records.length) return records;
    if (!this.state.productKey) return [];
    return [
      {
        location: locationList(this.state)[0] || "",
        damage: this.state.tearType,
        productKey: this.state.productKey,
        size: this.state.size,
        appliqueCount: this.state.appliqueCount,
      },
    ];
  }

  mappedProduct(key = this.state.productKey) {
    const handle = PRODUCT_MAP[this.family()]?.[key];
    return handle ? product(this.products, handle) : product(this.products, "");
  }

  checkoutProduct() {
    if (this.state.productKey === "whole_enchilada") {
      if (this.family() !== "denim") return this.mappedProduct("custom");
      return this.mappedProduct("whole_enchilada");
    }
    if (usesCustomDeposit(this.state)) return this.mappedProduct("custom");
    if (this.state.approach === "send_mend") return this.mappedProduct("send_mend");
    return this.mappedProduct();
  }

  customDepositVariant(prod = this.mappedProduct("custom")) {
    if (!prod?.id) return null;
    return visibleVariants(prod)[0] || prod.variants?.[0] || null;
  }

  variantFor(prod, size = this.state.size, record) {
    if (!prod?.id) return null;
    const key = record?.productKey || this.state.productKey;
    if (key === "custom" || key === "whole_enchilada") {
      return this.customDepositVariant(prod);
    }
    if (this.state.approach === "send_mend" && key !== "whole_enchilada" && key !== "custom") {
      return findVariant(prod, sendMendOption(key, record?.damage || this.state.tearType));
    }
    if (key === "appliques") {
      return findVariant(prod, record?.appliqueCount || this.state.appliqueCount);
    }
    if (key === "leaf" && this.family() === "knit") {
      return findVariant(prod, this.state.stitchDesign === "Leaf" ? "" : this.state.stitchDesign) || visibleVariants(prod)[0] || null;
    }
    if (key === "leaf") {
      return findVariant(prod, leafSizeValue(record?.size || size), leafMaterialValue(this.state.material));
    }
    if (key === "embroidered") {
      return findVariant(prod, this.state.stitchDesign || record?.size || size);
    }
    return findVariant(prod, record?.size || size);
  }

  resolved() {
    const prod = this.checkoutProduct();
    return { product: prod, variant: this.variantFor(prod) };
  }

  selectedSizes() {
    const records = this.ensureAreaRecords().filter((record) => areaNeedsMeasure(record, this.state));
    if (records.length) return records.map((record) => record.size).filter((size) => size && size !== "over6");
    const count = this.repairAreaTotal();
    if (count === 1) return this.state.size && this.state.size !== "over6" ? [this.state.size] : [];
    return (this.state.areaSizes || []).slice(0, count).filter((size) => size && size !== "over6");
  }

  measureProduct(record) {
    const key = record?.productKey || this.state.productKey;
    if (key === "patch" || key === "tear" || key === "leaf" || key === "trim") return this.mappedProduct(key);
    const family = this.family();
    const kind = effectiveMeasureKind(this.state);
    if (kind === "patch") return this.mappedProduct("patch");
    return this.mappedProduct(family === "knit" ? "trim" : "tear");
  }

  measurePrice() {
    const records = this.ensureAreaRecords().filter(
      (record) => areaNeedsMeasure(record, this.state) && record.size && record.size !== "over6"
    );
    if (records.length) {
      const total = records.reduce((sum, record) => {
        const variant = this.variantFor(this.measureProduct(record), record.size, record);
        return sum + Number(variant?.price || 0);
      }, 0);
      return total ? formatMoney(total) : "";
    }
    const prod = this.measureProduct();
    const sizes = this.selectedSizes();
    const variants = sizes.map((size) => findVariant(prod, size)).filter(Boolean);
    if (!variants.length) return "";
    if (variants.length === 1) return formatMoney(variants[0].price);
    return formatMoney(variants.reduce((sum, variant) => sum + Number(variant.price || 0), 0));
  }

  stepSkipped(id) {
    if (id === 3) return true;
    if (id === 5 && skipMeasure(this.state)) return true;
    if (id === 4 && skipProducts(this.state)) return true;
    if (id === 6 && skipDetails(this.state)) return true;
    return false;
  }

  visibleSteps() {
    return STEPS.filter((step) => !this.stepSkipped(step.id));
  }

  skipPhotosStep() {
    if (this.state.route || this.state.step !== 3) return;
    if (skipProducts(this.state)) {
      this.state.step = skipDetails(this.state) ? 7 : 6;
      return;
    }
    this.state.step = 4;
  }

  skipDetailsStep() {
    if (this.state.route) return;
    if (this.state.step === 6 && skipDetails(this.state)) this.state.step = 7;
  }

  stepClickable(id) {
    const maxStep = Math.max(Number(this.state.maxStep) || 1, this.state.step);
    if (id === this.state.step) return false;
    if (id < 1 || id > maxStep) return false;
    if (this.stepSkipped(id)) return false;
    return true;
  }

  goToStep(id) {
    const step = Number(id);
    if (!this.stepClickable(step)) return;
    this.showError("");
    this.state.route = "";
    this.state.step = step;
    this.render();
  }

  onProgressClick(event) {
    const btn = event.target.closest("[data-progress-step]");
    if (!btn || !this.progress.contains(btn)) return;
    this.goToStep(btn.dataset.progressStep);
  }

  onBack() {
    this.showError("");
    if (this.state.route) {
      const previous = this.state.route;
      this.state.route = "";
      this.state.step = previous === "oversize" ? 5 : previous === "help" ? 1 : 2;
      this.render();
      return;
    }
    if (this.state.step === 7) {
      if (skipDetails(this.state)) {
        this.state.step = skipProducts(this.state) ? 2 : skipMeasure(this.state) ? 4 : 5;
      } else {
        this.state.step = 6;
      }
    } else if (this.state.step === 6 && skipProducts(this.state)) {
      this.state.step = 2;
    } else if (this.state.step === 4 && this.state.approach !== "send_mend" && this.state.areaIndex > 0) {
      this.state.areaIndex -= 1;
      this.applyAreaToState();
      this.render();
      return;
    } else if (this.state.step === 6 && skipMeasure(this.state)) {
      this.state.step = 4;
    } else if (this.state.step === 5) {
      this.state.step = 4;
      this.state.areaIndex = Math.max(0, this.repairAreaTotal() - 1);
      this.applyAreaToState();
    } else if (this.state.step === 4) {
      this.state.step = 2;
    } else {
      this.state.step = Math.max(1, this.state.step - 1);
    }
    if (this.state.step === 3) this.state.step = 2;
    this.render();
  }

  onPanelClick(event) {
    const choice = event.target.closest("[data-choice]");
    const fieldWrap = event.target.closest("[data-field]");
    const field = fieldWrap?.dataset.field;
    const areaIndex = fieldWrap?.dataset.area;
    if (choice && field === "areaSize" && areaIndex != null) {
      const index = Number(areaIndex);
      const sizes = [...(this.state.areaSizes || [])];
      sizes[index] = choice.dataset.choice;
      this.state.areaSizes = sizes;
      this.state.size = sizes[0] || "";
      this.ensureAreaRecords();
      if (this.state.areaRecords[index]) this.state.areaRecords[index].size = choice.dataset.choice;
      this.render();
      return;
    }
    if (choice && field === "size") {
      this.state.size = choice.dataset.choice;
      this.syncCurrentArea({ size: choice.dataset.choice });
      this.render();
      return;
    }
    if (event.target.closest("[data-edit-step]")) {
      this.goToStep(event.target.closest("[data-edit-step]").dataset.editStep);
      return;
    }
    if (choice && field === "location") {
      this.state.location = [choice.dataset.choice];
      this.render();
      return;
    }
    if (choice && field === "areaLocation") {
      this.syncCurrentArea({ location: choice.dataset.choice });
      this.clearInvalidAreaRepair();
      this.render();
      return;
    }
    if (choice && field === "areaDamage") {
      this.syncCurrentArea({ damage: choice.dataset.choice });
      this.clearInvalidAreaRepair();
      this.render();
      return;
    }
    if (event.target.closest("[data-repair-details]")) {
      event.preventDefault();
      const card = event.target.closest(".repair-form__product");
      if (card) {
        const open = card.classList.toggle("is-open");
        const btn = card.querySelector("[data-repair-details]");
        if (btn) btn.setAttribute("aria-expanded", open ? "true" : "false");
      }
      return;
    }
    if (event.target.closest("[data-choose-repair]")) {
      const key = event.target.closest("[data-choose-repair]").dataset.chooseRepair;
      this.state.productKey = key;
      this.state.stitchDesign = key === "leaf" && this.family() === "knit" ? "Leaf" : "";
      this.state.customStitch = "";
      this.state.motifCount = "";
      this.state.appliqueCount = "";
      this.state.patchWanted = key === "patch" ? "Yes" : this.state.patchWanted;
      this.state.metallic = "";
      this.state.buttonhole = "No";
      this.state.patchMaterial = "";
      this.state.patchPlacement = "";
      this.state.patchColor = "";
      this.state.patchColorFamily = "";
      this.state.repairDirection = "";
      this.state.notes = "";
      this.state.consultType = "";
      this.syncCurrentArea({ productKey: key, size: "", appliqueCount: "" });
      if (metallicColorEntry(this.state.threadColor)) this.state.threadColor = "";
      this.render();
      return;
    }
    if (choice && field) {
      this.state[field] = choice.dataset.choice;
      if (field === "itemType" || field === "material" || field === "construction") {
        if (field === "material" && choice.dataset.choice !== "Cotton") this.state.construction = "";
        if (field === "itemType" && !needsConstructionChoice({ ...this.state, itemType: choice.dataset.choice })) {
          if (!(choice.dataset.choice === "Sweater or Knit" && this.state.material === "Cotton")) {
            this.state.construction = "";
          }
        }
        syncConstruction(this.state);
        this.state.family = inferFamily(this.state);
        this.clearInvalidAreaRepair();
        this.ensureAreaRecords().forEach((record) => {
          if (record.productKey && !repairAllowed(this.state.family, record.productKey, record.location, record.damage)) {
            record.productKey = "";
            record.size = "";
            record.appliqueCount = "";
          }
        });
        if (this.state.productKey === "whole_enchilada" && this.state.family !== "denim") {
          this.state.productKey = "";
        }
      }
      if (field === "threadColor") {
        this.state.threadColor = threadColorName(choice.dataset.choice);
      }
      if (field === "itemType") {
        const allowed = locationsFor(this.state.itemType);
        this.state.location = locationList(this.state).filter((item) => allowed.includes(item));
        if (Array.isArray(this.state.areaRecords)) {
          this.state.areaRecords = this.state.areaRecords.map((record) => ({
            ...record,
            location: allowed.includes(record.location) ? record.location : "",
          }));
        }
        if (this.state.material && !materialsFor(this.state.itemType).includes(this.state.material)) {
          this.state.material = "";
        }
      }
      if (field === "areas") {
        this.state.areaIndex = 0;
        this.state.areaRecords = [];
        this.ensureAreaRecords();
      }
      if (field === "approach") {
        if (choice.dataset.choice === "whole_enchilada") this.state.productKey = "whole_enchilada";
        else if (choice.dataset.choice === "custom") this.state.productKey = "custom";
        else if (["whole_enchilada", "custom"].includes(this.state.productKey)) this.state.productKey = "";
      }
      if (field === "measureKind") {
        this.state.size = "";
        this.state.areaSizes = [];
      }
      if (field === "threadColor") {
        this.state.metallicRequest = "";
      }
      if (field === "productKey") {
        this.state.stitchDesign = choice.dataset.choice === "leaf" && this.family() === "knit" ? "Leaf" : "";
        this.state.customStitch = "";
        this.state.motifCount = "";
        this.state.appliqueCount = "";
        this.state.patchWanted = choice.dataset.choice === "patch" ? "Yes" : this.state.patchWanted;
        this.state.metallic = "";
        this.state.buttonhole = "No";
        this.state.patchMaterial = "";
        this.state.patchPlacement = "";
        this.state.patchColor = "";
        this.state.patchColorFamily = "";
        this.state.repairDirection = "";
        this.state.notes = "";
        this.state.consultType = "";
        this.syncCurrentArea({ productKey: choice.dataset.choice, size: "", appliqueCount: "" });
        if (metallicColorEntry(this.state.threadColor)) this.state.threadColor = "";
      }
      if (field === "appliqueCount") {
        const value = choice.dataset.choice;
        if (value === "4+") {
          this.syncCurrentArea({ productKey: "custom", appliqueCount: value });
          this.state.productKey = "custom";
        } else {
          this.syncCurrentArea({ appliqueCount: value, productKey: "appliques" });
          this.state.productKey = "appliques";
        }
      }
      if (field === "patchWanted" && choice.dataset.choice === "No") {
        this.state.patchPlacement = "";
        this.state.patchMaterial = "";
        this.state.patchColor = "";
        this.state.patchColorFamily = "";
        this.state.buttonhole = "No";
      }
      if (field === "patchMaterial") {
        this.state.patchColor = "";
        this.state.patchColorFamily = "";
      }
      if (field === "patchColorFamily") this.state.patchColor = "";
      if (field === "patchPlacement") {
        if (this.state.patchPlacement !== "Under-Mend") this.state.buttonhole = "No";
        if (isPerimeterStitch(this.state.stitchDesign)) this.state.stitchDesign = "";
      }
      this.render();
    }
    if (event.target.closest("[data-remove-photo]")) {
      const key = event.target.closest("[data-remove-photo]").dataset.removePhoto;
      this.state.photos[key] = "";
      this.render();
    }
    if (event.target.closest("[data-switch-send-mend]")) {
      this.state.approach = "send_mend";
      this.state.measureKind = "";
      this.state.size = "";
      this.state.areaSizes = [];
      this.ensureAreaRecords().forEach((record) => {
        record.size = "";
      });
      this.state.route = "";
      const detailsReady = this.detailsFlow()
        .filter((id) => id !== "notes")
        .every((id) => this.detailComplete(id));
      this.state.step = detailsReady && this.state.productKey ? 7 : 6;
      this.render();
      return;
    }
    if (event.target.closest("[data-start]")) {
      const approach = event.target.closest("[data-start]").dataset.start;
      if (approach === "help") {
        this.state.route = "help";
        this.render();
        return;
      }
      this.state.approach = approach;
      this.state.acknowledged = true;
      this.state.step = 2;
      this.render();
      return;
    }
    if (event.target.closest("[data-consult-live]") || event.target.closest("[data-consult]")) {
      this.addConsultAndCheckout();
      return;
    }
    if (event.target.closest("[data-see-options]")) {
      this.state.route = "";
      this.state.productKey = "custom";
      this.state.step = skipDetails(this.state) ? 7 : 6;
      this.render();
    }
  }

  onPanelChange(event) {
    if (event.target.dataset.input === "acknowledged") {
      this.state.acknowledged = event.target.checked;
    }
    if (event.target.dataset.input === "notes") {
      this.state.notes = event.target.value;
    }
    this.updatePrice();
    this.persistFunnel();
  }

  validateStep() {
    const s = this.state;
    if (s.step === 1) return "Choose how you’d like to start.";
    if (s.step === 2) {
      if (!s.itemType || !s.material || !s.stretch || !locationList(s).length || !s.areas) {
        return "Choose an option to continue.";
      }
      if (needsConstructionChoice(s) && !s.construction) return "Choose whether this cotton is woven or knit.";
    }
    if (s.step === 4 && !skipProducts(s)) {
      const area = this.currentArea();
      if (!area.location) return "Choose where this area is.";
      if (!area.damage) return "Choose what kind of damage it is.";
      if (!area.productKey) return "Choose a repair.";
      if (area.productKey === "appliques" && !area.appliqueCount) return "Choose how many appliqués.";
    }
    if (s.step === 5 && !skipMeasure(s) && !needsOversize(s)) {
      const needed = this.ensureAreaRecords().filter((record) => areaNeedsMeasure(record, s));
      if (needed.some((record) => !record.size)) return "Choose a measurement or size for each repair.";
    }
    if (s.step === 6) {
      const wantsPatch = s.productKey === "patch";
      if (wantsPatch && !s.patchPlacement) return "Choose Over-Patch or Under-Mend.";
      if (wantsPatch && this.patchNeedsMaterialChoice() && !s.patchMaterial) {
        return "Choose a patch material.";
      }
      if (wantsPatch && this.patchNeedsFamilyChoice() && !s.patchColorFamily) {
        return "Choose a patch color family.";
      }
      if (wantsPatch && this.patchNeedsColorChoice() && !s.patchColor) {
        return "Choose a patch color.";
      }
      if (s.productKey === "patch" && !s.stitchDesign) return "Choose a Standard or Premium stitch.";
      if (s.productKey === "tear" && !s.stitchDesign) return "Choose a stitch design.";
      if ((s.productKey === "embroidered" || s.productKey === "appliques") && !s.stitchDesign) {
        return "Choose Roses, Stars or Raindrops.";
      }
      if (!["custom", "whole_enchilada"].includes(s.productKey) && !s.threadColor) return "Choose a thread color.";
      if (allowsMetallic(s) && !s.metallic) return metallicMode(s) === "accent" ? "Choose a metallic accent." : "Choose a metallic thread.";
      if (["patch", "tear", "leaf", "embroidered", "appliques"].includes(s.productKey) && !s.repairDirection) {
        return "Choose Tonal or Pop.";
      }
      if (s.productKey === "whole_enchilada" && !s.consultType) {
        return "Choose Digital Consult or Live Consult.";
      }
    }
    return "";
  }

  async onNext() {
    const error = this.validateStep();
    if (error) {
      this.showError(error);
      return;
    }
    this.showError("");

    if (this.state.step === 2) {
      this.state.family = inferFamily(this.state);
      if (eligibility(this.state) !== "ok") {
        this.state.route = "consult";
        this.render();
        return;
      }
    }

    if (this.state.step === 2 && skipProducts(this.state)) {
      this.state.step = skipDetails(this.state) ? 7 : 6;
      this.render();
      return;
    }

    if (this.state.step === 2) {
      this.state.areaIndex = 0;
      this.state.areaRecords = [];
      this.ensureAreaRecords();
    }

    if (this.state.step === 4 && !skipProducts(this.state)) {
      const area = this.currentArea();
      if (area.productKey === "custom") {
        this.state.step = 7;
        this.render();
        return;
      }
      if (area.productKey === "whole_enchilada") {
        this.state.step = 6;
        this.render();
        return;
      }
      if (this.state.approach !== "send_mend" && this.state.areaIndex < this.repairAreaTotal() - 1) {
        this.state.areaIndex += 1;
        this.applyAreaToState();
        this.render();
        return;
      }
      this.applyAreaToState(0);
      if (skipMeasure(this.state)) {
        this.state.step = skipDetails(this.state) ? 7 : 6;
        this.render();
        return;
      }
    }

    if (this.state.step === 5 && needsOversize(this.state)) {
      this.state.route = "oversize";
      this.render();
      return;
    }

    if (this.state.step === 7) {
      await this.submit();
      return;
    }

    this.state.step += 1;
    this.render();
  }

  async submit() {
    const items = this.checkoutItems();
    if (!items.length) {
      this.showError("This repair is missing a matching product variant. Go back and check your selections.");
      return;
    }
    return this.addAndCheckout(items);
  }

  areaSummary(records = this.checkoutRecords()) {
    return records
      .map((record, index) =>
        `Area ${index + 1}: ${[
          record.location,
          tearTypeLabel(record.damage),
          record.productKey,
          displaySize(record.size) || record.appliqueCount,
        ]
          .filter(Boolean)
          .join(" · ")}`
      )
      .join(" | ");
  }

  customDepositCheckoutItems() {
    const prod = this.mappedProduct("custom");
    const variant = this.customDepositVariant(prod);
    if (!prod?.id || !variant) return [];
    const records = this.checkoutRecords();
    const properties = this.lineItemProperties(prod, variant, "", records[0] || null);
    properties["Intended repair"] = "custom";
    const sizes = records.map((record) => displaySize(record.size) || record.appliqueCount).filter(Boolean);
    if (sizes.length) properties["Repair size"] = sizes.join(", ");
    if (records.length > 1) properties["All repair areas"] = this.areaSummary(records);
    if (isOverSixInches(this.state)) properties["Over 6 inches"] = "Yes";
    properties["Consult booking"] = CONSULT_BOOKING_URL;
    const items = [{ id: variant.id, quantity: 1, properties }];
    this.appendAddOns(items, prod);
    return items;
  }

  checkoutItems() {
    if (usesCustomDeposit(this.state)) return this.customDepositCheckoutItems();

    const records = this.checkoutRecords();
    if (!records.length) {
      const { product: prod, variant } = this.resolved();
      if (!prod?.id || !variant) return [];
      const items = [{ id: variant.id, quantity: 1, properties: this.lineItemProperties(prod, variant) }];
      this.appendAddOns(items, prod);
      return items;
    }

    if (this.state.approach === "send_mend" || records.every((record) => ["whole_enchilada", "custom"].includes(record.productKey))) {
      const key = records[0].productKey;
      const prod = key === "send_mend" ? this.mappedProduct("send_mend") : this.mappedProduct(key);
      const checkoutProd =
        this.state.approach === "send_mend" && key !== "whole_enchilada" && key !== "custom"
          ? this.mappedProduct("send_mend")
          : prod;
      const variant = this.variantFor(checkoutProd, records[0].size, records[0]);
      if (!checkoutProd?.id || !variant) return [];
      const properties = this.lineItemProperties(checkoutProd, variant, "", records[0]);
      if (records.length > 1) properties["All repair areas"] = this.areaSummary(records);
      const items = [{ id: variant.id, quantity: 1, properties }];
      this.appendAddOns(items, checkoutProd);
      return items;
    }

    const items = [];
    records.forEach((record, index) => {
      const prod = this.state.approach === "send_mend" ? this.mappedProduct("send_mend") : this.mappedProduct(record.productKey);
      const variant = this.variantFor(prod, record.size, record);
      if (!prod?.id || !variant) return;
      items.push({
        id: variant.id,
        quantity: 1,
        properties: this.lineItemProperties(
          prod,
          variant,
          records.length > 1 ? `Repair Area ${index + 1}` : "",
          record
        ),
      });
    });
    if (!items.length) return [];

    const patchRecord = records.find((record) => record.productKey === "patch");
    if (patchRecord && this.state.buttonhole === "Yes") {
      const addOn = product(this.products, "buttonhole-stitch-around-perimeter");
      const addOnVariant = addOn.variants?.[0];
      if (addOnVariant) {
        items.push({
          id: addOnVariant.id,
          quantity: 1,
          properties: {
            "Added with": this.mappedProduct("patch").title,
            "Thread color": threadColorName(this.state.threadColor),
          },
        });
      }
    }
    this.appendAddOns(items, this.mappedProduct(records[0].productKey));
    return items;
  }

  appendAddOns(items, prod) {
    const areas = this.repairAreaCount();
    if (this.state.productKey === "patch" && isPremiumStitch(this.state.stitchDesign)) {
      const addOn = this.premiumAddOn();
      const addOnVariant = addOn.variants?.[0];
      if (addOnVariant) {
        items.push({
          id: addOnVariant.id,
          quantity: areas,
          properties: {
            "Added with": prod?.title,
            "Stitch design": this.state.stitchDesign,
          },
        });
      }
    }
    if (allowsMetallic(this.state) && isMetallicThread(this.state)) {
      const addOn = this.metallicAddOn();
      const addOnVariant = addOn.variants?.[0];
      if (addOnVariant) {
        items.push({
          id: addOnVariant.id,
          quantity: areas,
          properties: {
            "Added with": prod?.title,
            "Metallic thread": this.state.metallic,
          },
        });
      }
    }
    if (!this.needsLiveConsultFee()) return;
    const consult = this.consultProduct();
    const consultVariant = this.liveConsultVariant();
    if (!consultVariant?.id) return;
    items.push({
      id: consultVariant.id,
      quantity: 1,
      properties: {
        "Added with": prod?.title,
        Consult: "Live Consult",
        "Consult booking": CONSULT_BOOKING_URL,
      },
    });
  }

  persistFunnel() {
    persistFormState(this.state);
  }

  resetBusy() {
    if (!this.nextBtn) return;
    this.nextBtn.dataset.busy = "false";
    this.nextBtn.textContent = this.nextLabel();
    this.syncNextButton();
  }

  async addConsultAndCheckout() {
    const variant = this.liveConsultVariant();
    if (!variant?.id) {
      this.showError("The 30 Minute Consult is not available yet. Please try again in a moment.");
      return;
    }
    return this.addAndCheckout([
      {
        id: variant.id,
        quantity: 1,
        properties: { "Consult booking": CONSULT_BOOKING_URL },
      },
    ]);
  }

  openCartDrawer() {
    const cart = document.querySelector(".modal--cart, .popup--cart-notification");
    if (cart && typeof cart.open === "function") {
      if (!cart.shown) cart.open();
      return true;
    }
    const trigger = document.querySelector('[data-a11y-dialog-show="modal-cart"]');
    if (trigger) {
      trigger.click();
      return true;
    }
    return false;
  }

  async addAndCheckout(items) {
    this.persistFunnel();
    this.nextBtn.dataset.busy = "true";
    this.nextBtn.disabled = true;
    this.nextBtn.textContent = "Adding to cart…";
    const root = this.config.root === "/" ? "" : this.config.root.replace(/\/$/, "");
    try {
      const response = await fetch(`${root}/cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || "Could not add this repair to cart.");
      }
      try {
        await Cart.update({ updates: {} });
        await Cart.resetVariantsBeingAdded();
      } catch (_) {
        // The server cart already has the items; still open the slider.
      }
      if (!this.openCartDrawer()) {
        window.location.href = `${root}/cart`;
        return;
      }
      this.resetBusy();
    } catch (error) {
      this.resetBusy();
      this.showError(error.message || "Something went wrong. Please try again.");
    }
  }

  lineItemProperties(prod, variant, areaLabel, record) {
    const properties = {
      Garment: this.state.itemType,
      Material: this.state.material,
      Stretch: this.state.stretch,
      "Damage location": record?.location || locationLabel(this.state),
      "Repair areas": this.state.areas,
      Approach: approachLabel(this.state.approach),
      "Measure type": this.state.measureKind,
      "Damage type": tearTypeLabel(record?.damage || this.state.tearType),
      "Repair size": variant?.option1 || record?.size || this.state.size,
      "Repair area": areaLabel,
      "Intended repair": record?.productKey || this.state.productKey,
      Appliqués: record?.appliqueCount || this.state.appliqueCount,
      "Stitch design":
        this.state.productKey === "custom" || this.state.productKey === "whole_enchilada"
          ? ""
          : this.state.stitchDesign || this.state.customStitch,
      "Premium stitch":
        this.state.productKey === "patch" && isPremiumStitch(this.state.stitchDesign)
          ? `${formatMoney(this.premiumCents())} × ${this.repairAreaCount()}`
          : "",
      Motifs: this.state.motifCount,
      "Thread color": threadColorName(this.state.threadColor),
      "Metallic thread": allowsMetallic(this.state) ? this.state.metallic || "None" : "",
      "Patch material": this.state.productKey === "patch" ? this.patchMaterialLabel(this.state.patchMaterial) : "",
      "Patch placement": this.state.patchPlacement,
      "Perimeter stitch":
        this.state.productKey === "patch" && this.state.patchPlacement === "Over-Patch"
          ? "Running Stitch (included)"
          : "",
      "Patch color": this.state.productKey === "patch" ? this.patchColorSummary() : "",
      "Overall repair direction": this.state.repairDirection,
      Notes: this.state.notes,
      "Buttonhole stitch": this.state.buttonhole,
      Consult: this.state.consultType,
      "Consult booking":
        isCustomShopifyProduct(prod) || this.state.productKey === "custom"
          ? CONSULT_BOOKING_URL
          : this.state.consultType === "Live Consult"
            ? CONSULT_BOOKING_URL
            : this.state.consultType === "Digital Consult"
              ? `${this.config.root === "/" ? "" : this.config.root.replace(/\/$/, "")}/pages/digital-consult`
              : "",
      Product: prod?.title,
      Variant: variant?.title,
      Construction: this.state.construction ? (this.state.construction === "knit" ? "Knit" : "Woven") : "",
      "Photo 1 — Full garment": this.state.photos.full,
      "Photo 2 — Damage": this.state.photos.damage,
      "Photo 3 — Scale": this.state.photos.scale,
    };
    Object.keys(properties).forEach((key) => {
      if (!properties[key]) delete properties[key];
    });
    return properties;
  }

  updatePrice() {
    const extra = this.addOnTotalCents();
    if ((this.state.step === 5 || !this.state.productKey) && this.selectedSizes().length) {
      const prod = this.measureProduct();
      const price = this.measurePrice();
      if (prod?.id && price) {
        this.priceEl.innerHTML = `<span>${escapeHtml(prod.title)}</span><strong>${escapeHtml(price)}</strong>`;
        return;
      }
    }

    const { product: prod, variant } = this.resolved();
    if (usesCustomDeposit(this.state)) {
      const custom = this.mappedProduct("custom");
      const deposit = this.customDepositVariant(custom);
      const cents = Number(deposit?.price || custom.minPrice || 0);
      if (cents) {
        const lines = [`<span>${escapeHtml(custom.title)}</span><strong>${escapeHtml(formatMoney(cents))}</strong>`];
        if (extra) {
          const consult = this.consultProduct();
          lines.push(
            `<span>${escapeHtml(consult.title || "Live Consult")}</span><strong>${escapeHtml(formatMoney(extra))}</strong>`
          );
        }
        this.priceEl.innerHTML = lines.join("");
        return;
      }
    }
    if (this.state.approach === "send_mend" && this.state.productKey !== "whole_enchilada" && this.state.productKey !== "custom") {
      const send = this.mappedProduct("send_mend");
      const sendVariant = this.variantFor(send);
      this.priceEl.innerHTML = sendVariant
        ? `<span>${escapeHtml(send.title)}</span><strong>${escapeHtml(formatMoney(Number(sendVariant.price) + extra))}</strong>`
        : send.minPriceFormatted
          ? `<span>Starting at</span><strong>${escapeHtml(formatMoney(Number(send.minPrice) + extra))}</strong>`
          : "";
      return;
    }

    const records = this.checkoutRecords().filter((record) => record.productKey && record.productKey !== "custom" && record.productKey !== "whole_enchilada");
    if (this.state.approach !== "send_mend" && records.length > 1) {
      const total = records.reduce((sum, record) => {
        const recordProd = this.mappedProduct(record.productKey);
        return sum + Number(this.variantFor(recordProd, record.size, record)?.price || 0);
      }, 0);
      if (total) {
        this.priceEl.innerHTML = `<span>${records.length} repair areas</span><strong>${escapeHtml(formatMoney(total + extra))}</strong>`;
        return;
      }
    }
    const sizes = this.selectedSizes();
    if (prod && sizes.length > 1 && this.state.productKey !== "whole_enchilada" && this.state.productKey !== "custom") {
      const total = sizes.reduce((sum, size) => sum + Number(this.variantFor(prod, size)?.price || 0), 0);
      this.priceEl.innerHTML = `<span>${escapeHtml(prod.title)} · ${sizes.length} areas</span><strong>${escapeHtml(
        formatMoney(total + extra)
      )}</strong>`;
      return;
    }

    if (variant) {
      this.priceEl.innerHTML = `<span>${escapeHtml(prod.title)}</span><strong>${escapeHtml(
        formatMoney(Number(variant.price) + extra)
      )}</strong>`;
      return;
    }
    if (prod?.minPriceFormatted) {
      this.priceEl.innerHTML = `<span>Starting at</span><strong>${escapeHtml(formatMoney(Number(prod.minPrice) + extra))}</strong>`;
      return;
    }
    this.priceEl.innerHTML = "";
  }

  paintProgress() {
    const chooser = this.state.step === 1 || this.state.route === "help";
    this.progressBar?.classList.toggle("is-chooser", chooser);
    if (this.progressBar) this.progressBar.hidden = chooser;
    const visible = this.visibleSteps();
    const current = visible.find((step) => step.id === this.state.step) || visible[0] || STEPS[0];
    const currentIndex = Math.max(0, visible.findIndex((step) => step.id === this.state.step));
    if (this.progressStatus) {
      this.progressStatus.hidden = chooser;
      this.progressStatus.textContent = chooser
        ? ""
        : `Step ${currentIndex + 1} of ${visible.length} · ${current.label}`;
    }
    if (!this.progress) return;
    if (chooser) {
      this.progress.innerHTML = "";
      return;
    }
    this.progress.innerHTML = visible.map((step, index) => {
      const isCurrent = this.state.step === step.id;
      const isSkipped = this.stepSkipped(step.id);
      const isDone = this.state.step > step.id && !isSkipped;
      const clickable = this.stepClickable(step.id);
      const marker = isDone
        ? `<svg class="repair-form__progress-check" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" d="M3.5 8.2 6.6 11.3 12.5 4.7"/></svg>`
        : String(index + 1);
      const inner = `
          <span class="repair-form__progress-num">${marker}</span>
          <span class="repair-form__progress-label">${step.label}</span>
      `;
      const body = clickable
        ? `<button type="button" class="repair-form__progress-btn" data-progress-step="${step.id}" aria-label="Go back to ${escapeHtml(
            step.label
          )}">${inner}</button>`
        : `<span class="repair-form__progress-btn repair-form__progress-btn--static">${inner}</span>`;
      return `
        <li class="repair-form__progress-item${isCurrent ? " is-current" : ""}${isDone ? " is-done" : ""}${
          isSkipped ? " is-skipped" : ""
        }${clickable ? " is-clickable" : ""}"${isCurrent ? ' aria-current="step"' : ""}>
          ${body}
        </li>
      `;
    }).join("");
  }

  nextLabel() {
    if (this.state.step === 7) return "Add Repair to Cart";
    return "Continue";
  }

  stickyOffset() {
    const header =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-section-height")) || 72;
    return header + 16;
  }

  scrollToFunnel() {
    const y = this.root.getBoundingClientRect().top + window.scrollY - this.stickyOffset();
    window.scrollTo({ top: Math.max(0, y), behavior: "auto" });
  }

  render() {
    this.skipPhotosStep();
    this.skipDetailsStep();
    const funnelKey = `${this.state.step}:${this.state.route || ""}:${this.state.areaIndex || 0}`;
    const shouldScroll = this._funnelKey != null && this._funnelKey !== funnelKey;
    this._funnelKey = funnelKey;

    this.state.maxStep = Math.min(7, Math.max(Number(this.state.maxStep) || 1, this.state.step));
    this.paintProgress();

    this.backBtn.hidden = this.state.step === 1 && !this.state.route;
    this.backBtn.disabled = this.backBtn.hidden;
    this.nextBtn.hidden = Boolean(this.state.route) || this.state.step === 1;
    if (this.navEl) this.navEl.hidden = this.backBtn.hidden && this.nextBtn.hidden;
    this.nextBtn.textContent = this.nextLabel();

    if (this.state.route === "help") {
      this.panel.innerHTML = this.renderHelp();
    } else if (this.state.route === "consult") {
      this.panel.innerHTML = this.renderConsult();
    } else if (this.state.route === "oversize") {
      this.panel.innerHTML = this.renderOversize();
    } else {
      const views = {
        1: () => this.renderIntro(),
        2: () => this.renderItem(),
        3: () => this.renderPhotos(),
        4: () => this.renderProducts(),
        5: () => this.renderMeasure(),
        6: () => this.renderDetails(),
        7: () => this.renderReviewStep(),
      };
      this.panel.innerHTML = views[this.state.step]();
    }

    this.updatePrice();
    this.syncNextButton();
    this.persistFunnel();
    if (this.state.step === 2 || this.state.step === 4 || this.state.step === 6) {
      const stage = this.panel.querySelector("[data-details-current]")?.getAttribute("data-details-current") || "done";
      const previousStage = this._detailsStage;
      const skipScroll =
        previousStage === "fabric" || previousStage === "denimPalette" || previousStage === "direction";
      const shouldFocus = previousStage != null && previousStage !== stage && !skipScroll;
      this._detailsStage = stage;
      if (shouldFocus && !shouldScroll) {
        requestAnimationFrame(() => this.scrollToCurrentDetail());
      }
    } else {
      this._detailsStage = null;
    }
    if (shouldScroll) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.scrollToFunnel());
      });
    }
  }

  scrollToCurrentDetail() {
    const current = this.panel.querySelector("[data-details-current]");
    if (!current) return;
    const previous = current.previousElementSibling;
    const reveal = previous?.querySelector(".repair-form__selection-preview") || current;
    const header = this.stickyOffset();
    const rect = reveal.getBoundingClientRect();
    const viewBottom = window.innerHeight - 160;
    if (rect.top >= header && rect.bottom <= viewBottom) return;
    const y = rect.top + window.scrollY - header - 8;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  }

  syncNextButton() {
    if (!this.nextBtn || this.nextBtn.hidden) return;
    if (this.nextBtn.dataset.busy === "true") return;
    this.nextBtn.disabled = false;
    this.nextBtn.setAttribute("aria-disabled", "false");
  }

  selfMeasureStartingPrice() {
    const handles = [
      PRODUCT_MAP.denim.tear,
      PRODUCT_MAP.denim.patch,
      PRODUCT_MAP.woven.tear,
      PRODUCT_MAP.woven.patch,
      PRODUCT_MAP.woven.leaf,
      PRODUCT_MAP.woven.appliques,
      PRODUCT_MAP.knit.trim,
      PRODUCT_MAP.knit.embroidered,
    ];
    const prices = handles
      .map((handle) => product(this.products, handle))
      .filter((prod) => prod.id && prod.minPrice)
      .sort((a, b) => a.minPrice - b.minPrice);
    return prices[0] || { minPrice: 0 };
  }

  sendMendDeposit() {
    const prices = ["denim", "woven", "knit"]
      .map((family) => product(this.products, PRODUCT_MAP[family].send_mend))
      .filter((prod) => prod.id && prod.minPrice)
      .sort((a, b) => a.minPrice - b.minPrice);
    return prices[0] || { minPrice: 0, minPriceFormatted: "" };
  }

  startingAt(cents) {
    if (!cents) return "";
    return `Starting at ${formatMoney(cents)}`;
  }

  pathMedia(key, fallbackAlt) {
    const image = this.config.pathImages?.[key];
    if (!image?.src) return "";
    return `<div class="repair-form__path-media"><img src="${escapeHtml(image.src)}" alt="${escapeHtml(image.alt || fallbackAlt)}" width="800" height="600"></div>`;
  }

  renderIntro() {
    const consultPrice = this.consultPrice();
    const root = this.config.root === "/" ? "" : this.config.root.replace(/\/$/, "");
    return `
      <div class="repair-form__intro-copy">
        <h2 class="repair-form__heading">Choose how to order your repair.</h2>
        <div class="repair-form__note">
          <p>Know what you want, and is the damage straightforward? Choose Measure & Mend. Not sure? Let’s Do This Together.<br class="repair-form__desktop-break"> A repair can start with a conversation. Send photos or talk it through live. We’ve got this.</p>
        </div>
      </div>
      <div class="repair-form__paths">
        <article class="repair-form__path">
          ${this.pathMedia("self_measure", "Measure & Mend")}
          <h3 class="repair-form__subheading">Measure & Mend</h3>
          <p class="repair-form__lede">You measure. Choose your details. We’ll take it from there.</p>
          <button type="button" class="c-btn c-btn--primary" data-start="self_measure">Build My Repair</button>
        </article>
        <article class="repair-form__path">
          ${this.pathMedia("send_mend", "Send & Mend")}
          <h3 class="repair-form__subheading">Send &amp; Mend</h3>
          <p class="repair-form__lede">Choose your repair and customizations, and we’ll measure for that repair and confirm the price before work begins. The ${
            this.sendMendDeposit().minPrice ? formatMoney(this.sendMendDeposit().minPrice) : "$80"
          } deposit is credited toward your approved repair. You choose the repair; we handle the measuring once it gets here.</p>
          <button type="button" class="c-btn c-btn--primary" data-start="send_mend">Send &amp; Mend</button>
        </article>
        <article class="repair-form__path">
          ${this.pathMedia("lets_do_this_together", "Let’s Do This Together")}
          <h3 class="repair-form__subheading">Let’s Do This Together</h3>
          <p class="repair-form__lede">Start with a free, photo-based Digital Consult or book a ${
            consultPrice ? `${consultPrice} ` : ""
          }Live Consult, credited toward your next repair. We’ll answer your questions and point you in the right direction, as if it were our own. No repair vocabulary required.</p>
          <a class="c-btn c-btn--primary" href="${root}/pages/lets-do-this-together">We’ve Got This</a>
        </article>
      </div>
      <div class="repair-form__note repair-form__guarantee">
        <p>Nothing is repaired without your approval. Tracked shipping is included both ways. Most standard repairs take 2–3 weeks after arrival and approval.<br class="repair-form__desktop-break"> Every repair includes a one-year reinforcement guarantee.</p>
      </div>
    `;
  }

  renderHelp() {
    const consultPrice = this.consultPrice();
    const fee = consultPrice ? `${consultPrice} ` : "";
    return `
      <h2 class="repair-form__heading">Let’s Do This Together</h2>
      <p class="repair-form__lede">Schedule a ${fee}live consultation if you’d rather talk it through.${
        consultPrice ? ` We credit the ${consultPrice} consultation fee toward your first repair.` : ""
      }</p>
      <article class="repair-form__path repair-form__path--full">
        <h3 class="repair-form__subheading">${consultPrice ? `${consultPrice} ` : ""}Live Consult</h3>
        <p class="repair-form__lede">Talk it through with us.${
          consultPrice ? ` The ${consultPrice} is credited toward your first repair.` : ""
        }</p>
        <button type="button" class="c-btn c-btn--primary" data-consult-live>Schedule a Live Consult</button>
      </article>
    `;
  }

  renderItem() {
    if (this.state.material && !materialsFor(this.state.itemType).includes(this.state.material)) {
      this.state.material = "";
    }
    syncConstruction(this.state);
    this.state.family = inferFamily(this.state);
    const allowedLocations = locationsFor(this.state.itemType);
    this.state.location = locationList(this.state).filter((item) => allowedLocations.includes(item));
    return `
      <h2 class="repair-form__heading">What are we working with?</h2>
      <p class="repair-form__lede">A few basics help us show repairs that make sense for your garment.</p>
      ${
        this.itemReached("itemType")
          ? fieldGroup(
              "What kind of item is it?",
              `<div class="repair-form__grid repair-form__grid--compact" data-field="itemType">
        ${ITEM_TYPES.map((item) => choiceButton(item, item, "", this.state.itemType === item)).join("")}
      </div>`,
              true,
              this.itemStage("itemType")
            )
          : ""
      }
      ${
        this.itemReached("material")
          ? fieldGroup(
              "What is the primary material?",
              `<div class="repair-form__grid repair-form__grid--compact" data-field="material">
        ${materialsFor(this.state.itemType)
          .map((item) => choiceButton(item, item, "", this.state.material === item))
          .join("")}
      </div>`,
              true,
              this.itemStage("material")
            )
          : ""
      }
      ${
        this.itemReached("construction")
          ? fieldGroup(
              "Is this cotton woven or knit?",
              `<div class="repair-form__grid repair-form__grid--compact" data-field="construction">
        ${["woven", "knit"]
          .map((item) =>
            choiceButton(item, item === "woven" ? "Woven" : "Knit", "", this.state.construction === item)
          )
          .join("")}
      </div>`,
              true,
              this.itemStage("construction")
            )
          : ""
      }
      ${
        this.itemReached("stretch")
          ? fieldGroup(
              "Does the fabric have significant stretch?",
              `<div class="repair-form__grid repair-form__grid--compact" data-field="stretch">
        ${STRETCH.map((item) => choiceButton(item, item, "", this.state.stretch === item)).join("")}
      </div>`,
              true,
              this.itemStage("stretch")
            )
          : ""
      }
      ${
        this.itemReached("location")
          ? fieldGroup(
              "Where is the damage?",
              `<div class="repair-form__grid repair-form__grid--compact" data-field="location">
        ${locationsFor(this.state.itemType)
          .map((item) => choiceButton(item, item, "", locationList(this.state)[0] === item))
          .join("")}
      </div>`,
              true,
              this.itemStage("location")
            )
          : ""
      }
      ${
        this.itemReached("areas")
          ? fieldGroup(
              "How many separate repair areas are there?",
              `<div class="repair-form__grid repair-form__grid--compact" data-field="areas">
        ${AREA_COUNTS.map((item) => choiceButton(item, item, "", this.state.areas === item)).join("")}
      </div>`,
              true,
              this.itemStage("areas")
            )
          : ""
      }
    `;
  }

  renderConsult() {
    const root = this.config.root === "/" ? "" : this.config.root.replace(/\/$/, "");
    if (eligibility(this.state) === "unsupported") {
      return `
        <h2 class="repair-form__heading">Unsupported item</h2>
        <p class="repair-form__lede">This item isn’t a fit for our current repair service. We’d rather tell you clearly than pretend every repair belongs in the same system.</p>
        <a class="c-btn c-btn--primary" href="${root}/pages/contact">Contact Us</a>
      `;
    }
    return `
      <h2 class="repair-form__heading">A closer look</h2>
      <p class="repair-form__lede">A few repairs need a second look. Your answers are saved. Send us a few clear photos and we’ll take it from here.</p>
      <a class="c-btn c-btn--primary" href="${root}/pages/digital-consult">Start a Free Digital Consult</a>
    `;
  }

  renderPhotos() {
    const selfMeasure = this.state.approach === "self_measure";
    const sendMendNote = selfMeasure
      ? ""
      : `<p class="repair-form__note">We’ll use these photos to confirm eligibility and understand the repair. You do not need to measure the damage—we’ll do that when it arrives.</p>`;
    return `
      <h2 class="repair-form__heading">A Picture Is Worth a Thousand Words.</h2>
      <p class="repair-form__lede">A few good photos help. Please use good light!</p>
      ${sendMendNote}
      ${PHOTO_SLOTS.map((slot) => {
        const url = this.state.photos[slot.key];
        const required = photoSlotRequired(this.state, slot);
        const flag = required ? "Required" : "Optional";
        return `
          <article class="repair-form__photo-card${required ? "" : " is-optional"}">
            <header class="repair-form__photo-card-head">
              <h3 class="repair-form__photo-kicker"><span>${slot.number}</span> / ${escapeHtml(slot.heading)}</h3>
              <p class="repair-form__photo-flag${required ? " is-required" : " is-optional"}">${flag}</p>
            </header>
            <p class="repair-form__lede">${escapeHtml(slot.text)}</p>
            ${
              url
                ? `<div class="repair-form__photo-preview">
                    <img src="${escapeHtml(url)}-/preview/280x280/" alt="${escapeHtml(slot.heading)}">
                    <button type="button" class="repair-form__text-btn" data-remove-photo="${slot.key}">Replace photo</button>
                  </div>`
                : `<input type="hidden" id="repair-photo-${slot.key}" data-photo-slot="${slot.key}" data-upload-label="${escapeHtml(
                    slot.button
                  )}" role="uploadcare-uploader" data-images-only="true" data-input-accept="image/jpeg,image/png,image/heic,.heic">`
            }
          </article>
        `;
      }).join("")}
      <p class="repair-form__note">Accepted formats: JPG, PNG or HEIC.</p>
    `;
  }

  initUploaders() {
    const key = this.config.uploadcarePublicKey;
    if (!key) {
      this.panel.insertAdjacentHTML(
        "beforeend",
        `<p class="repair-form__note">Add an Uploadcare public key in Theme settings → Repair form to enable uploads.</p>`
      );
      return;
    }
    const start = () => {
      if (!window.uploadcare) return;
      window.UPLOADCARE_PUBLIC_KEY = key;
      PHOTO_SLOTS.forEach((slot) => {
        const input = this.panel.querySelector(`#repair-photo-${slot.key}`);
        if (!input) return;
        const widget = window.uploadcare.Widget(input, { imagesOnly: true, previewStep: true });
        widget.inputElement?.setAttribute("aria-label", slot.button);
        const widgetRoot = widget.inputElement?.parentElement;
        const widgetButton = widgetRoot?.querySelector(".uploadcare--widget__button_type_open");
        if (widgetButton) {
          widgetButton.textContent = slot.button;
          widgetButton.classList.add("c-btn", "c-btn--primary");
          widgetButton.style.backgroundColor = "#212121";
          widgetButton.style.color = "#ffffff";
          widgetButton.style.borderRadius = "999px";
          widgetButton.style.border = "0";
          widgetButton.style.boxShadow = "none";
        }
        const cancelButton = widgetRoot?.querySelector(".uploadcare--widget__button_type_cancel");
        if (cancelButton) cancelButton.hidden = true;
        widget.onChange((file) => {
          if (!file) return;
          file
            .done((info) => {
              this.state.photos[slot.key] = info.cdnUrl || info.originalUrl;
              this.render();
            })
            .fail(() => {
              this.showError("That photo did not upload. Your other answers are still saved — try that image again.");
            });
        });
      });
    };
    if (window.uploadcare) {
      start();
      return;
    }
    const existing = document.querySelector("script[data-uploadcare]");
    if (existing) {
      existing.addEventListener("load", start, { once: true });
      return;
    }
    window.UPLOADCARE_PUBLIC_KEY = key;
    const script = document.createElement("script");
    script.src = "https://ucarecdn.com/libs/widget/3.x/uploadcare.full.min.js";
    script.dataset.uploadcare = "true";
    script.addEventListener("load", start, { once: true });
    document.head.appendChild(script);
  }

  renderMeasure() {
    const root = this.config.root === "/" ? "" : this.config.root.replace(/\/$/, "");
    const all = this.ensureAreaRecords();
    const measurable = all
      .map((record, areaNumber) => ({ record, areaNumber }))
      .filter(({ record }) => areaNeedsMeasure(record, this.state));
    const cluster = [locationLabel(this.state), ...all.map((record) => record.location)].includes(
      "Cluster of Small Holes or Spots"
    );

    const sizeGrid = (record, areaNumber, values, prod, allowOver) => {
      const selected = record.size || "";
      return `
      <div class="repair-form__grid repair-form__grid--compact" data-field="areaSize" data-area="${areaNumber}">
        ${values
          .map((value) => {
            const variant = this.variantFor(prod, value, { ...record, size: value });
            return choiceButton(value, value, priceLabel(variant), selected === value);
          })
          .join("")}
        ${allowOver ? choiceButton("over6", "More than 6 inches", "Requires a custom plan", selected === "over6") : ""}
      </div>
    `;
    };

    const sizeFields = measurable
      .map(({ record, areaNumber }) => {
        const prod = this.measureProduct(record);
        const isPatch = record.productKey === "patch";
        const values = uniqueOptionValues(prod, "option1").filter((value) => {
          if (this.family() === "woven" && isPatch && /xxl/i.test(value)) return false;
          return true;
        });
        const isLeaf = record.productKey === "leaf";
        const kind = isPatch ? "Patch size" : isLeaf ? "Leaf size" : "Length";
        const parts = [`Repair Area ${areaNumber + 1}`];
        if (record.location) parts.push(record.location);
        parts.push(kind);
        const label = all.length === 1 ? kind : parts.join(" · ");
        const note = isPatch
          ? "Choose the smallest size that completely contains the damaged and weakened area."
          : "Measure from one end of the complete damaged area to the other. Round up to the next whole inch.";
        return fieldGroup(
          label,
          `<p class="repair-form__note">${escapeHtml(note)}</p>${sizeGrid(record, areaNumber, values, prod, !isLeaf)}`,
          true
        );
      })
      .join("");

    return `
      <h2 class="repair-form__heading">Measure the whole weakened area</h2>
      <p class="repair-form__lede">A good measurement helps us match the repair to the damage. Include thin, frayed, or weakened fabric around the opening—not only the visible hole. Measure the whole weakened area, not just what has already given way.</p>
      <p class="repair-form__note">For pro tips and best practices, see our <a href="${root}/pages/how-to-size-your-repair">measuring guide</a>.</p>
      ${
        cluster
          ? `<p class="repair-form__note">One localized cluster of small holes, stains, worn spots, or a combination counts as one repair area. Measure the overall footprint of the cluster. Physically separate damage locations are separate repair areas.</p>`
          : ""
      }
      ${sizeFields}
      ${
        this.selectedSizes().length
          ? `<p class="repair-form__note">Your selection: <strong>${escapeHtml(
              this.selectedSizes().join(", ")
            )}</strong> · Price: <strong>${escapeHtml(this.measurePrice())}</strong></p>`
          : ""
      }
      ${
        this.state.approach === "self_measure"
          ? `<p class="repair-form__note repair-form__send-mend-switch"><button type="button" class="repair-form__text-btn" data-switch-send-mend>Prefer us to measure? Switch to Send &amp; Mend</button></p>`
          : ""
      }
    `;
  }

  renderOversize() {
    const root = this.config.root === "/" ? "" : this.config.root.replace(/\/$/, "");
    return `
      <h2 class="repair-form__heading">This repair needs a custom plan</h2>
      <p class="repair-form__lede">For damage over 6 inches or more than 4 holes or spots, we’ll make a custom plan with you. Choose Custom Repair to continue, or start a free Digital Consult if you want to talk it through first.</p>
      <p class="repair-form__switch">
        <button type="button" class="c-btn c-btn--primary" data-see-options>Choose Custom Repair</button>
      </p>
      <p class="repair-form__switch">
        <a class="c-btn c-btn--primary" href="${root}/pages/digital-consult">Start a Digital Consult</a>
      </p>
    `;
  }

  consultProduct() {
    return product(this.products, "30-minute-consult");
  }

  liveConsultVariant() {
    const consult = this.consultProduct();
    return (
      findVariant(consult, "Live (credited toward next repair)") ||
      (consult.variants || []).find((variant) => /live/i.test(`${variant.option1 || ""} ${variant.title || ""}`) && Number(variant.price) > 0) ||
      null
    );
  }

  consultPrice() {
    return priceLabel(this.liveConsultVariant() || this.consultProduct());
  }

  needsLiveConsultFee() {
    if (this.state.productKey === "custom") return false;
    return usesCustomDeposit(this.state) && this.state.consultType === "Live Consult";
  }

  startAt(handle, deposit) {
    if (!handle) return "";
    const prod = product(this.products, handle);
    if (!prod.id || !prod.minPrice) return "";
    if (deposit) {
      return `We collect a ${formatMoney(prod.minPrice)} deposit that is credited toward your approved repair.`;
    }
    return `Starting at ${formatMoney(prod.minPrice)}`;
  }

  customDepositLine(family) {
    const handle = PRODUCT_MAP[family]?.custom;
    const prod = product(this.products, handle);
    const deposit = prod.minPrice ? formatMoney(prod.minPrice) : "$100";
    return `We collect a ${deposit} deposit that is credited toward your approved repair.`;
  }

  customRepairCopy(family) {
    const handle = PRODUCT_MAP[family]?.custom;
    const prod = product(this.products, handle);
    const deposit = prod.minPrice ? formatMoney(prod.minPrice) : "$100";
    return `Starts at $150. ${this.customDepositLine(family)} We’ll confirm the final scope and price after checkout and before work begins. If we determine the repair isn’t feasible, we’ll refund the ${deposit} deposit in full.`;
  }

  flatRateMeta(handle) {
    const prod = product(this.products, handle);
    if (!prod.id || !prod.minPrice) return "";
    return `${formatMoney(prod.minPrice)} flat, paid in full, no deposit.`;
  }

  eligibleProducts() {
    const family = this.family();
    if (family !== "denim" && this.state.productKey === "whole_enchilada") {
      this.state.productKey = "custom";
      this.syncCurrentArea({ productKey: "custom" });
    }
    const cards = [];
    const area = this.currentArea();

    if (family === "denim") {
      cards.push(
        {
          key: "tear",
          title: "Clean Tear",
          summary: "Close tears and split seams with visible stitching that reinforces the fabric and turns damage into detail.",
          details: `A stitch-only repair for a clean rip, split seam or tear without missing material. ${areaLimitCopy("denim")}`,
          meta: this.startAt(PRODUCT_MAP.denim.tear, false),
        },
        {
          key: "patch",
          title: "Patch & Mend",
          summary: "Cover holes and worn areas with a fabric patch and decorative stitching that adds strength and character.",
          details: `A reinforced patch and visible stitching for holes, worn areas or missing material. ${areaLimitCopy("denim")}`,
          meta: this.startAt(PRODUCT_MAP.denim.patch, false),
        },
        {
          key: "whole_enchilada",
          title: "Whole Enchilada",
          summary: "Transform your denim with a combination of patches, stitching, and creative repairs throughout.",
          details: "Full-garment denim repair. Paid in full. Choose Digital Consult or Live Consult before checkout. No individual repair-area measurements and no additional consult fee.",
          meta: this.flatRateMeta(PRODUCT_MAP.denim.whole_enchilada),
        },
        {
          key: "custom",
          title: "Custom Denim",
          summary: `${CUSTOM_STARTING_AT}. Give more complicated denim damage a one-of-a-kind repair with custom patches and stitching designed around your garment.`,
          details: this.customRepairCopy("denim"),
          meta: this.customDepositLine("denim"),
        }
      );
    }

    if (family === "woven") {
      cards.push(
        {
          key: "tear",
          title: "Clean Tear",
          summary: "Close tears and split seams with visible stitching that reinforces the fabric and turns damage into detail.",
          details: `A stitch-only repair for a clean rip, split seam or tear without missing material. ${areaLimitCopy("woven")}`,
          meta: this.startAt(PRODUCT_MAP.woven.tear, false),
        },
        {
          key: "patch",
          title: "Patch & Mend",
          summary: "Cover holes and worn areas with a fabric patch and decorative stitching that adds strength and character.",
          details: `A reinforced patch and visible stitching for holes, worn areas or missing material. ${areaLimitCopy("woven")}`,
          meta: this.startAt(PRODUCT_MAP.woven.patch, false),
        },
        {
          key: "leaf",
          title: "Embroidered Leaf",
          summary: "Cover small areas of damage with delicate leaf embroidery inspired by nature.",
          details: `A stitched leaf repair for cotton, linen and silk. Silk uses the silk product price. ${areaLimitCopy("woven")}`,
          meta: this.startAt(PRODUCT_MAP.woven.leaf, false),
        },
        {
          key: "appliques",
          title: "Embroidered Appliqués",
          summary: "Cover small holes with decorative embroidered appliqués that turn damage into a design detail.",
          details: `Hand-embroidered appliqués. Choose 1, 2, or 3. Four or more needs Custom Woven. ${areaLimitCopy("woven")}`,
          meta: this.startAt(PRODUCT_MAP.woven.appliques, false),
        },
        {
          key: "custom",
          title: "Custom Woven",
          summary: `${CUSTOM_STARTING_AT}. Give more complicated damage a thoughtful, one-of-a-kind repair designed for your garment.`,
          details: this.customRepairCopy("woven"),
          meta: this.customDepositLine("woven"),
        }
      );
    }

    if (family === "knit") {
      cards.push(
        {
          key: "trim",
          title: "Clean Trim",
          summary: "Repair damaged sweater edges, cuffs, and hems with stitching that restores their shape and finish.",
          details: `A clean, reinforced finish for a damaged knit edge, cuff, hem or opening. ${areaLimitCopy("knit")}`,
          meta: this.startAt(PRODUCT_MAP.knit.trim, false),
        },
        {
          key: "embroidered",
          title: "Embroidered Knit",
          summary: "Mend holes in sweaters with decorative embroidery that adds color, texture, and character.",
          details: `An embroidered repair using Roses, Stars or Raindrops. Shape sets the live product price. ${areaLimitCopy("knit")}`,
          meta: this.startAt(PRODUCT_MAP.knit.embroidered, false),
        },
        {
          key: "leaf",
          title: "Embroidered Leaf",
          summary: "Cover small areas of damage with delicate leaf embroidery inspired by nature.",
          details: `A leaf motif on an embroidered knit repair. ${areaLimitCopy("knit")}`,
          meta: this.startAt(PRODUCT_MAP.knit.embroidered, false),
        },
        {
          key: "custom",
          title: "Custom Sweater",
          summary: `${CUSTOM_STARTING_AT}. Restore sweaters with a custom combination of mending and embroidery designed around the damage.`,
          details: this.customRepairCopy("knit"),
          meta: this.customDepositLine("knit"),
        }
      );
    }

    return cards.filter(
      (card) =>
        this.mappedProduct(card.key).id &&
        repairAllowed(family, card.key, area.location, area.damage)
    );
  }

  renderProducts() {
    this.ensureAreaRecords();
    const sendMend = this.state.approach === "send_mend";
    const area = this.currentArea();
    if (sendMend && !area.location) {
      const fromItem = locationList(this.state)[0] || "";
      if (fromItem) this.syncCurrentArea({ location: fromItem });
    }
    const cards = this.eligibleProducts();
    const total = this.repairAreaTotal();
    const index = this.state.areaIndex + 1;
    const selectedKey = area.productKey || "";
    const appliqueSelected = selectedKey === "appliques" || area.appliqueCount === "4+";
    const showDamage = Boolean(area.location);
    const showRepair = showDamage && Boolean(area.damage);
    const showApplique = showRepair && appliqueSelected;
    return `
      <h2 class="repair-form__heading">What needs repair?</h2>
      <p class="repair-form__lede">${
        sendMend
          ? "Choose the repair that fits. If you have more than one area, we’ll include that count in your Send & Mend order and customize it once."
          : "Choose the repair that fits the spot. Each area gets its own selection. More than one repair area? We’ll take them one at a time."
      }</p>
      ${total > 1 && !sendMend ? `<p class="repair-form__area-kicker">Repair Area ${index} of ${total}</p>` : ""}
      ${
        sendMend
          ? ""
          : fieldGroup(
              "Where is this area?",
              `<div class="repair-form__grid repair-form__grid--compact" data-field="areaLocation">
          ${locationsFor(this.state.itemType)
            .map((item) => choiceButton(item, item, "", area.location === item))
            .join("")}
        </div>`,
              true,
              area.location ? "" : "areaLocation"
            )
      }
      ${
        showDamage
          ? fieldGroup(
              "What kind of damage is it?",
              `<div class="repair-form__grid" data-field="areaDamage">
          ${damageTypesFor(this.family())
            .map((item) => choiceButton(item.key, item.title, item.text, area.damage === item.key))
            .join("")}
        </div>`,
              true,
              area.damage ? "" : "areaDamage"
            )
          : ""
      }
      ${
        showRepair
          ? fieldGroup(
              "Choose a repair",
              `${cards.length ? "" : `<p class="repair-form__note">No standard repair matches this area. Start a Digital Consult and we’ll help you plan the next step.</p><p class="repair-form__switch"><a class="c-btn c-btn--primary" href="${this.config.root === "/" ? "" : this.config.root.replace(/\/$/, "")}/pages/digital-consult">Start a Digital Consult</a></p>`}
              <div class="repair-form__products">
        ${cards
          .map(
            (card) => `
              <article class="repair-form__product${selectedKey === card.key ? " is-selected" : ""}">
                <div class="repair-form__product-head">
                  <span class="repair-form__choice-title">${escapeHtml(card.title)}</span>
                  <span class="repair-form__price-tag">${escapeHtml(card.meta)}</span>
                </div>
                <p class="repair-form__product-summary">${escapeHtml(card.summary)}</p>
                <button type="button" class="repair-form__text-btn" data-repair-details aria-expanded="false">Repair details</button>
                <p class="repair-form__product-details">${escapeHtml(card.details)}</p>
                <button type="button" class="c-btn ${selectedKey === card.key ? "c-btn--primary" : "c-btn--hollow"} repair-form__product-cta" data-choose-repair="${escapeHtml(card.key)}">${
                  selectedKey === card.key ? "Selected" : `Choose ${escapeHtml(card.title)}`
                }</button>
              </article>
            `
          )
          .join("")}
      </div>`,
              true,
              selectedKey || !cards.length ? "" : "productKey"
            )
          : ""
      }
      ${
        showApplique
          ? fieldGroup(
              "How many appliqués?",
              `<p class="repair-form__note">1, 2, or 3 appliqués are standard. Four or more needs Custom Woven Mend.</p>
              <div class="repair-form__grid repair-form__grid--compact" data-field="appliqueCount">
                ${APPLIQUE_COUNTS.map((item) => {
                  const appliqueProd = this.mappedProduct("appliques");
                  const variant = item.key === "4+" ? null : findVariant(appliqueProd, item.key);
                  return choiceButton(
                    item.key,
                    item.title,
                    item.key === "4+" ? "Custom Woven" : priceLabel(variant),
                    area.appliqueCount === item.key
                  );
                }).join("")}
              </div>`,
              true,
              area.appliqueCount ? "" : "appliqueCount"
            )
          : ""
      }
    `;
  }

  renderDetails() {
    this.syncPatchDefaults();
    const patch = this.state.productKey === "patch";
    const custom = this.state.productKey === "custom" || this.state.productKey === "whole_enchilada";
    const embroidered = this.state.productKey === "embroidered" || this.state.productKey === "appliques";
    const leaf = this.state.productKey === "leaf";
    const trim = this.state.productKey === "trim";
    const tear = this.state.productKey === "tear";
    const showButtonhole = patch && this.state.patchWanted === "Yes" && this.state.patchPlacement === "Under-Mend";
    if (
      patch &&
      this.state.stitchDesign &&
      ![...STANDARD_STITCHES, ...PREMIUM_STITCHES].includes(this.state.stitchDesign)
    ) {
      this.state.stitchDesign = "";
    }
    const materialChoices = this.patchMaterialChoices();
    const patterned = this.patternedGroups();
    const familyGroup = patterned.find((item) => item.key === this.state.patchColorFamily);
    if (custom) this.state.customStitch = "";

    return `
      <h2 class="repair-form__heading">${custom ? "Confirm your custom repair." : "Make it yours."}</h2>
      <p class="repair-form__lede">${
        custom
          ? "No preset customization. Choose Digital Consult or Live Consult before checkout, then add anything you want us to know."
          : "Choose the stitches, thread and patch options that make your garment 1 of 1. Keep it quiet, let it show, or land somewhere in between. The repair does the work either way."
      }</p>
      ${
        custom
          ? `<p class="repair-form__note">No preset customization. Choose Digital Consult or Live Consult before checkout; we’ll confirm the custom plan after your order is placed.</p>`
          : this.funStuffLink()
      }
      ${
        patch && this.detailsReached("placement")
          ? fieldGroup(
              "Choose Your Patch",
              `<p class="repair-form__note">Patch options depend on your garment’s material. If your repair needs a patch, we’ll show you the choices that work best for it.</p>
              <div class="repair-form__photos" data-field="patchPlacement">
              ${PATCH_PLACEMENTS.map((item) =>
                imageChoice(
                  item.key,
                  item.title,
                  this.placementSrc(item.key),
                  this.state.patchPlacement === item.key,
                  item.text
                )
              ).join("")}
            </div>`,
              true,
              this.detailsStage("placement")
            )
          : ""
      }
      ${
        patch && this.state.patchWanted === "Yes" && this.detailsReached("placement") && this.state.patchPlacement && this.state.material === "Silk"
          ? fieldGroup(
              "Your Silk Patch",
              `<p class="repair-form__note">If your repair requires a patch, we’ll select the most suitable silk or coordinating material and confirm it with you before we begin.</p>`
            )
          : ""
      }
      ${
        patch && this.state.patchWanted === "Yes" && this.detailsReached("material") && this.patchNeedsMaterialChoice()
          ? fieldGroup(
              "Choose Your Patch Material",
              `<p class="repair-form__note">${
                this.family() === "denim" || this.state.material === "Denim"
                  ? "Would you like a denim patch or a patterned cotton woven patch?"
                  : "Would you like us to find a close linen match or add a patterned cotton woven patch?"
              }</p>
              <div class="repair-form__photos" data-field="patchMaterial">
              ${materialChoices
                .map((item) =>
                  imageChoice(
                    item.key,
                    item.title,
                    item.src,
                    this.state.patchMaterial === item.key,
                    item.key === "denim-patch"
                      ? "Keep the patch in the denim family, then choose the patch palette."
                      : item.key === "matching-linen"
                        ? "We’ll select the closest compatible linen available to complement your garment."
                        : "Add pattern and contrast with a cotton woven patch. Choose your fabric from the available options."
                  )
                )
                .join("")}
            </div>`,
              true,
              this.detailsStage("material")
            )
          : ""
      }
      ${
        patch && this.state.patchWanted === "Yes" && this.detailsReached("family") && !this.patchNeedsMaterialChoice() && this.state.patchMaterial === "patterned-cotton"
          ? `<p class="repair-form__note"><strong>Choose Your Patch.</strong> Select a patterned cotton woven fabric from the available options.</p>`
          : ""
      }
      ${
        patch && this.state.patchWanted === "Yes" && this.detailsReached("denimPalette")
          ? fieldGroup(
              "Denim patch palette",
              `<p class="repair-form__note">Keep the patch in the denim family, then choose the patch palette. This palette is only for the denim patch. Overall Repair Direction later is Tonal or Pop for the whole repair.</p>
              <div class="repair-form__photos" data-field="patchColor">
                ${this.denimPaletteChoices()
                  .map((item) =>
                    imageChoice(item.key, item.title, item.src, this.state.patchColor === item.key, item.text)
                  )
                  .join("")}
              </div>`,
              true,
              this.detailsStage("denimPalette")
            )
          : ""
      }
      ${
        patch && this.state.patchWanted === "Yes" && this.detailsReached("family")
          ? fieldGroup(
              "Patch color family",
              `<div class="repair-form__family-btns" data-field="patchColorFamily">
                ${patterned
                  .map(
                    (item) => `
                  <button type="button" class="repair-form__family-btn${
                    this.state.patchColorFamily === item.key ? " is-selected" : ""
                  }" data-choice="${escapeHtml(item.key)}" style="background-image:url('${escapeHtml(item.src || "")}')">
                    ${choiceMark()}
                    <span>${escapeHtml(item.title)}</span>
                  </button>`
                  )
                  .join("")}
              </div>`,
              true,
              this.detailsStage("family")
            )
          : ""
      }
      ${
        patch && this.state.patchWanted === "Yes" && this.detailsReached("fabric") && familyGroup
          ? fieldGroup(
              "Patch fabric",
              (() => {
                const familyTitle = PATTERNED_TITLES[familyGroup.key] || familyGroup.title;
                const selectedSwatch =
                  (familyGroup.swatches || []).find((item) => item.key === this.state.patchColor) ||
                  (familyGroup.swatches || [])[0];
                const previewSrc = selectedSwatch?.src || familyGroup.src || "";
                return `
              ${
                this.state.patchColor
                  ? `<p class="repair-form__note">Selected: <strong>${escapeHtml(familyTitle)}</strong></p>`
                  : `<p class="repair-form__note">Choose your fabric from the available options.</p>`
              }
              ${
                previewSrc
                  ? `<div class="repair-form__fabric-preview"><img src="${escapeHtml(previewSrc)}" alt="${escapeHtml(
                      familyTitle
                    )}"></div>`
                  : ""
              }
              <div class="repair-form__photos repair-form__photos--swatches" data-field="patchColor">
                ${(familyGroup.swatches || [])
                  .map((item) =>
                    imageChoice(item.key, familyTitle, item.src, this.state.patchColor === item.key, "", {
                      showTitle: false,
                    })
                  )
                  .join("")}
              </div>`;
              })(),
              true,
              this.detailsStage("fabric")
            )
          : ""
      }
      ${
        patch && this.detailsReached("stitch")
          ? fieldGroup(
              "Stitch Design",
              `<p class="repair-form__note">Choose one Standard or Premium overlay and view one clear visual example.</p>
            ${this.stitchLibrary("patch")}
            ${this.state.stitchDesign ? this.selectionPreview(this.state.stitchDesign) : ""}`,
              true,
              this.detailsStage("stitch")
            )
          : ""
      }
      ${
        tear && this.detailsReached("stitch")
          ? fieldGroup(
              "Stitch Design",
              `<p class="repair-form__note">Choose one design and view one clear visual example.</p>
            ${this.stitchLibrary("tear")}
            ${this.state.stitchDesign ? this.selectionPreview(this.state.stitchDesign) : ""}`,
              true,
              this.detailsStage("stitch")
            )
          : ""
      }
      ${
        embroidered && this.detailsReached("stitch")
          ? fieldGroup(
              "Embroidery",
              `<p class="repair-form__note">${
                this.state.productKey === "appliques"
                  ? "Choose Roses, Stars or Raindrops for the appliqués."
                  : "Choose Roses, Stars or Raindrops. Each option is a real repair."
              }</p>
            <div class="repair-form__designs" data-field="stitchDesign">
              ${KNIT_MOTIFS.map((item) => designButton(item, this.motifImage(item), this.state.stitchDesign === item)).join("")}
            </div>
            ${this.state.stitchDesign ? this.selectionPreview(this.state.stitchDesign) : ""}`,
              true,
              this.detailsStage("stitch")
            )
          : ""
      }
      ${
        leaf && this.detailsReached("visual")
          ? fieldGroup("Visual direction", this.selectionPreview("", this.leafExample()), false, this.detailsStage("visual"))
          : ""
      }
      ${
        trim && this.detailsReached("visual")
          ? fieldGroup("Visual direction", this.trimPreview(), false, this.detailsStage("visual"))
          : ""
      }
      ${this.detailsReached("thread") ? this.threadField(this.detailsStage("thread")) : ""}
      ${this.detailsReached("metallic") ? this.metallicField(this.detailsStage("metallic")) : ""}
      ${
        this.detailsReached("consult")
          ? fieldGroup(
              "Choose Digital Consult or Live Consult before checkout",
              `<p class="repair-form__note">${
                this.state.productKey === "whole_enchilada"
                  ? "We’ll confirm the full-garment repair plan after checkout. There is no additional consult fee."
                  : "We’ll confirm the custom plan after your order is placed."
              }</p>
              <div class="repair-form__grid" data-field="consultType">
                ${choiceButton(
                  "Digital Consult",
                  "Digital Consult",
                  "Send a few clear photos and tell us what you’re thinking.",
                  this.state.consultType === "Digital Consult"
                )}
                ${choiceButton(
                  "Live Consult",
                  "Live Consult",
                  this.state.productKey === "whole_enchilada"
                    ? "A 30-minute Google Meet after checkout. Included in your Whole Enchilada."
                    : this.consultPrice()
                    ? `Adds ${this.consultPrice()}, credited toward your next repair. The consult happens after checkout.`
                    : "Book a live conversation. The consult happens after checkout.",
                  this.state.consultType === "Live Consult"
                )}
              </div>`,
              true,
              this.detailsStage("consult")
            )
          : ""
      }
      ${
        ["patch", "tear", "leaf", "embroidered", "appliques"].includes(this.state.productKey) &&
        this.detailsReached("direction")
          ? fieldGroup(
              "Overall Repair Direction",
              `<p class="repair-form__note">${
                patch
                  ? "Your visual direction applies to the complete repair—including the patch, stitch and thread—not only the patch."
                  : "Choose Tonal or Pop for the complete repair, including thread and the stitch we plan with you."
              }</p>
              <div class="repair-form__photos repair-form__photos--pair" data-field="repairDirection">
                ${REPAIR_DIRECTIONS.map((item) =>
                  imageChoice(
                    item.key,
                    item.title,
                    this.mediaSrc("directions", item.key),
                    this.state.repairDirection === item.key,
                    item.text
                  )
                ).join("")}
              </div>`,
              true,
              this.detailsStage("direction")
            )
          : ""
      }
      ${
        this.detailsReached("notes") && showButtonhole
          ? fieldGroup(
              "Buttonhole stitch around the perimeter",
              `<p class="repair-form__note">Optional${
                (() => {
                  const price = priceLabel(product(this.products, "buttonhole-stitch-around-perimeter"));
                  return price ? ` ${price}` : "";
                })()
              } add-on for Under-Mend only.</p>
              <div class="repair-form__grid" data-field="buttonhole">
              ${choiceButton("No", "No", "No added product.", this.state.buttonhole === "No")}
              ${choiceButton(
                "Yes",
                "Yes",
                `Adds ${priceLabel(product(this.products, "buttonhole-stitch-around-perimeter"))} and matches the selected thread color.`,
                this.state.buttonhole === "Yes"
              )}
            </div>
            ${this.state.buttonhole === "Yes" ? this.selectionPreview("", this.mediaSrc("examples", "buttonhole")) : ""}`
            )
          : ""
      }
      ${this.detailsReached("notes") ? this.notesField(this.detailsStage("notes")) : ""}
    `;
  }

  renderReviewStep() {
    this.syncPatchDefaults();
    const patch = this.state.productKey === "patch";
    const leaf = this.state.productKey === "leaf";
    const trim = this.state.productKey === "trim";
    const stitchName = this.state.stitchDesign || this.state.customStitch;
    const { product: prod, variant } = this.resolved();
    return `
      <h2 class="repair-form__heading">Review your repair</h2>
      <p class="repair-form__lede">Give it one last look. Check each choice, then send your repair on its way. Garment, repair, details, price. Done.</p>
      ${this.renderReview(prod, variant, stitchName, patch, leaf, trim)}
    `;
  }

  reviewPaymentCopy(prod, variant) {
    const price = priceLabel(variant) || priceLabel(prod);
    const sendMend = this.sendMendDeposit();
    const sendMendPrice = sendMend.minPrice ? formatMoney(sendMend.minPrice) : "";
    const custom = this.mappedProduct("custom");
    const customPrice = custom.minPrice ? formatMoney(custom.minPrice) : price;
    const whole = this.mappedProduct("whole_enchilada");
    const wholePrice = whole.minPrice ? formatMoney(whole.minPrice) : price;
    const consult = this.state.consultType ? ` Consult: ${this.state.consultType}.` : "";
    if (this.state.productKey === "whole_enchilada") {
      return `Due today: ${wholePrice || price}, payable in full.${consult}`;
    }
    if (usesCustomDeposit(this.state)) {
      const when =
        this.state.approach === "send_mend"
          ? " After your garment arrives, we’ll confirm the final scope and price before work begins."
          : " We’ll confirm the final scope and price after checkout and before work begins.";
      return `Due today: ${customPrice} credited deposit. Starting at $150.${when} If we determine the repair isn’t feasible, the deposit is refunded in full.`;
    }
    if (this.state.approach === "send_mend") {
      const due = sendMendPrice || formatMoney(sendMend.minPrice) || "$80";
      return `Due today: ${due} credited deposit. After your garment arrives, we’ll measure for the repair you selected and send the price for your approval.`;
    }
    const records = this.checkoutRecords();
    const baseCents = records.length
      ? records.reduce((sum, record) => {
          const recordProd = this.mappedProduct(record.productKey);
          return sum + Number(this.variantFor(recordProd, record.size, record)?.price || 0);
        }, 0)
      : Number(variant?.price || prod?.minPrice || 0);
    const totalCents = baseCents + this.addOnTotalCents();
    const total = totalCents ? formatMoney(totalCents) : price;
    return total ? `Repair total: ${total}. Due today: ${total}.` : "";
  }

  renderReview(prod, variant, stitchName, patch, leaf, trim) {
    const records = this.checkoutRecords();
    const sizeLabel =
      this.state.approach === "send_mend"
        ? "Pending assessment"
        : records
            .map((record) => record.appliqueCount || displaySize(record.size))
            .filter(Boolean)
            .join(", ") ||
          this.selectedSizes().join(", ") ||
          this.state.size ||
          this.state.appliqueCount ||
          "";
    const repairLabel = records
      .map((record, index) => {
        const title = this.mappedProduct(record.productKey)?.title || record.productKey;
        const parts = [record.location, tearTypeLabel(record.damage), title].filter(Boolean);
        return records.length > 1 ? `Area ${index + 1}: ${parts.join(" · ")}` : parts.join(" · ");
      })
      .join(" · ");
    const patchBits = patch
      ? [
          this.state.patchWanted === "No" ? "No patch" : "",
          this.state.patchPlacement,
          this.state.patchPlacement === "Over-Patch" ? "Running Stitch included" : "",
          this.patchMaterialLabel(this.state.patchMaterial),
          this.patchColorSummary(),
        ]
          .filter(Boolean)
          .join(" · ")
      : "";
    const rows = [
      ["Path", approachLabel(this.state.approach), 1],
      ["Garment", [this.state.itemType, this.state.material, this.state.construction === "knit" ? "Knit" : this.state.construction === "woven" ? "Woven" : ""].filter(Boolean).join(" · "), 2],
      ["Repair", repairLabel || [locationLabel(this.state), tearTypeLabel(this.state.tearType), prod?.title].filter(Boolean).join(" · "), 4],
      ["Size", sizeLabel, skipMeasure(this.state) ? 0 : 5],
      ["Stitch", stitchName, 6],
      ["Thread", threadColorName(this.state.threadColor), 6],
      ["Metallic", isMetallicThread(this.state) ? this.state.metallic : "", 6],
      ["Direction", this.state.repairDirection, 6],
      ["Patch", patchBits, 6],
      ["Consult", this.state.consultType, 6],
      ["Price", this.reviewPaymentCopy(prod, variant), 0],
      ["Shipping", "Tracked shipping both ways is included.", 0],
    ].filter(([, value]) => value);

    const look =
      stitchName
        ? `<div class="repair-form__review-look">
            <p class="repair-form__review-product"><strong>Selected stitch</strong><span>${escapeHtml(stitchName)}</span></p>
            ${this.selectionPreview(stitchName, leaf ? this.leafExample() : "")}
          </div>`
        : leaf || trim
          ? `<div class="repair-form__review-look">${leaf ? this.selectionPreview("", this.leafExample()) : this.trimPreview()}</div>`
          : "";

    return `<div class="repair-form__review">
      <dl>
        ${rows
          .map(
            ([label, value, step]) => `
          <div class="repair-form__review-row">
            <dt>${escapeHtml(label)}</dt>
            <dd>${escapeHtml(value)}</dd>
            ${
              step && this.stepClickable(step)
                ? `<button type="button" class="repair-form__text-btn" data-edit-step="${step}">Edit</button>`
                : `<span></span>`
            }
          </div>`
          )
          .join("")}
      </dl>
      ${look}
      ${this.checkoutAddOns()
        .map(
          (addOn) => `
            <p class="repair-form__review-product">
              <strong>${escapeHtml(addOn.title)}</strong>
              <span>${escapeHtml(priceLabel(addOn))}</span>
            </p>
          `
        )
        .join("")}
      <p class="repair-form__review-note">Nothing is repaired without your approval. Most standard repairs take 2–3 weeks after arrival and approval. Tracked shipping is included both ways. Every repair includes a one-year reinforcement guarantee.</p>
    </div>`;
  }
}

function initRepairForms() {
  document.querySelectorAll("[data-repair-form]").forEach((root) => {
    if (root.dataset.ready) return;
    const configEl = root.querySelector("[data-repair-form-config]");
    if (!configEl) return;
    let config = {};
    try {
      config = JSON.parse(configEl.textContent);
    } catch (error) {
      console.error("Repair form config is invalid", error);
      return;
    }
    root.dataset.ready = "true";
    new RepairForm(root, config);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initRepairForms);
} else {
  initRepairForms();
}

document.addEventListener("shopify:section:load", initRepairForms);
